import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  Users, ArrowLeftRight, Wallet, ShieldCheck, Settings, TrendingUp, Store, IdCard, FileText, ReceiptText, DollarSign
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid,
} from 'recharts';

const CHART_COLORS = ['#047857', '#0d9488', '#0891b2', '#7c3aed', '#db2777', '#f59e0b'];

function StatusBadge({ status }) {
  const s = {
    verified: 'bg-emerald-100 text-emerald-800', admin: 'bg-blue-100 text-blue-800',
    user: 'bg-slate-100 text-slate-700', sub_admin: 'bg-violet-100 text-violet-800',
    pending: 'bg-amber-100 text-amber-800', suspended: 'bg-red-100 text-red-800',
    successful: 'bg-emerald-100 text-emerald-800', failed: 'bg-red-100 text-red-800',
    paid: 'bg-emerald-100 text-emerald-800', approved: 'bg-blue-100 text-blue-800',
    processing: 'bg-indigo-100 text-indigo-800',
    rejected: 'bg-red-100 text-red-800', cancelled: 'bg-slate-200 text-slate-700',
    refunded: 'bg-orange-100 text-orange-800', reversed: 'bg-purple-100 text-purple-800',
    not_submitted: 'bg-slate-100 text-slate-600',
    more_info_required: 'bg-blue-100 text-blue-800',
  };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${s[status] || 'bg-slate-100 text-slate-800'}`}>{status?.replace(/_/g, ' ')}</span>;
}

function SettlementBadge({ withdrawal }) {
  const mode = String(withdrawal?.settlement_mode || '').toLowerCase();
  const status = String(withdrawal?.settlement_status || '').toLowerCase();
  if (withdrawal?.settled_directly_to_merchant || mode === 'flutterwave_split') {
    const label = status === 'pending' ? 'Split settlement pending' : 'Split-settled to merchant';
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-800">{label}</span>;
  }
  if (mode === 'jjuma_wallet') {
    const label = status === 'pending_wallet_fallback' ? 'Wallet settlement pending' : 'Paid via JJuma wallet';
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-800">{label}</span>;
  }
  if (mode === 'manual') {
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-800">Manual review</span>;
  }
  return null;
}

function TransactionSettlementBadge({ txn }) {
  const mode = String(txn?.settlement_mode || '').toLowerCase();
  const status = String(txn?.settlement_status || '').toLowerCase();
  if (txn?.settled_directly_to_merchant || mode === 'flutterwave_split') {
    const label = status === 'pending' ? 'Split settlement pending' : 'Split-settled to merchant';
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-800">{label}</span>;
  }
  if (mode === 'jjuma_wallet') {
    const label = status === 'pending_wallet_fallback' ? 'Wallet settlement pending' : 'Paid via JJuma wallet';
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-800">{label}</span>;
  }
  if (mode === 'manual') {
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-800">Manual review</span>;
  }
  return null;
}

function WithdrawalSettlementSummary({ withdrawal }) {
  const mode = String(withdrawal?.settlement_mode || '').toLowerCase();
  if (withdrawal?.settled_directly_to_merchant || mode === 'flutterwave_split') return 'Split-settled to merchant';
  if (mode === 'jjuma_wallet') return 'Paid via JJuma wallet';
  if (mode === 'manual') return 'Manual review';
  return 'Settlement pending';
}

function normalizeText(value, fallback = '') {
  if (value == null || value === '') return fallback;
  return String(value).toLowerCase();
}

function formatAdminApiError(err, fallback = 'Failed to save settings') {
  const status = err?.response?.status;
  const data = err?.response?.data;
  const detail = data?.detail ?? data?.message ?? data?.error ?? data;
  if (detail == null && err?.message === 'Network Error') {
    return 'Unable to reach the server. Please check your connection and try again.';
  }
  if (status >= 500 && !detail) {
    return 'The server could not complete this request right now. Please try again in a moment.';
  }
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => item?.msg || item?.message || item?.detail || JSON.stringify(item))
      .filter(Boolean)
      .join(' ');
  }
  if (detail?.msg) return detail.msg;
  if (detail?.detail) return formatAdminApiError({ response: { status, data: { detail: detail.detail } } }, fallback);
  if (detail?.message) return detail.message;
  if (detail?.error) return typeof detail.error === 'string' ? detail.error : JSON.stringify(detail.error);
  if (typeof err?.message === 'string' && err.message) return err.message;
  return fallback;
}

function logAdminSaveFailure(action, err) {
  if (process.env.NODE_ENV !== 'production') {
    console.error(`[AdminPanel] ${action} failed`, {
      status: err?.response?.status,
      data: err?.response?.data,
    });
  }
}

function getUserRoleLabel(user) {
  if (normalizeText(user?.role, '') !== 'admin') return 'User';
  if (normalizeText(user?.admin_role, '') && normalizeText(user?.admin_role, '') !== 'super_admin') return 'Sub Admin';
  return 'Admin';
}

function getVerificationLabel(user) {
  const inferred = user?.verification_status
    || user?.account_status
    || (normalizeText(user?.role, '') === 'verified' ? 'verified' : '')
    || (normalizeText(user?.role, '') === 'pending' ? 'pending' : '')
    || (normalizeText(user?.role, '') === 'rejected' ? 'rejected' : '');
  return normalizeText(inferred, 'not_submitted');
}

function SmallBadge({ children, className = '' }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${className}`}>
      {children}
    </span>
  );
}

function OverviewTab() {
  const [data, setData] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [byCurrency, setByCurrency] = useState(null);
  const [summary, setSummary] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => { api.get('/admin/overview').then(r => setData(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    api.get(`/admin/analytics/timeseries?days=${days}&granularity=day`).then(r => setAnalytics(r.data)).catch(() => {});
    api.get(`/admin/analytics/by-currency?days=${days}`).then(r => setByCurrency(r.data)).catch(() => {});
    api.get(`/admin/analytics/summary?days=${days}`).then(r => setSummary(r.data)).catch(() => {});
  }, [days]);

  if (!data) return <div className="flex items-center justify-center h-40"><div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" /></div>;

  const stats = [
    { label: 'Total Users', value: data.total_users, icon: Users },
    { label: 'Verified', value: data.verified_users, icon: ShieldCheck },
    { label: 'Pending', value: data.pending_users, icon: ShieldCheck },
    { label: 'Transactions', value: data.total_transactions, icon: ArrowLeftRight },
    { label: 'Pending Withdrawals', value: data.pending_withdrawals, icon: Wallet },
    { label: 'Pending Verifications', value: data.pending_verifications, icon: ShieldCheck },
    { label: 'Total Stores', value: data.total_stores, icon: Store },
    { label: 'Total vCards', value: data.total_vcards, icon: IdCard },
    { label: 'Total Invoices', value: data.total_invoices, icon: FileText },
    { label: 'Total Orders', value: data.total_orders, icon: ReceiptText },
    { label: 'Package Revenue', value: Number(data.package_revenue || 0).toLocaleString(), icon: DollarSign },
  ];

  const pieData = (byCurrency?.by_currency || []).map((r, idx) => ({
    name: r.currency, value: r.volume || 0, color: CHART_COLORS[idx % CHART_COLORS.length],
  }));

  return (
    <div className="space-y-6 pt-1 md:pt-0">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
        {stats.map(({ label, value, icon: Icon }) => (
          <Card key={label} className="bg-white border border-slate-200 shadow-sm rounded-2xl">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-start justify-between gap-3 mb-2">
                <p className="text-[11px] sm:text-xs font-medium text-slate-500 leading-snug">{label}</p>
                <Icon size={15} className="text-slate-400 shrink-0 mt-0.5" />
              </div>
              <p className="text-2xl sm:text-2xl font-bold text-slate-900 mono-nums leading-tight" data-testid={`admin-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>{value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl" data-testid="analytics-summary-card">
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="text-lg" style={{ fontFamily: 'Outfit' }}>Revenue Analytics</CardTitle>
            <p className="text-xs text-slate-500 mt-0.5">Successful transaction volume & fee revenue</p>
          </div>
          <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v))}>
            <SelectTrigger className="w-full sm:w-32" data-testid="analytics-range-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last 365 days</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          {summary && (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 mb-4">
              <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-100">
                <p className="text-xs text-emerald-700">Transactions ({days}d)</p>
                <p className="text-xl font-bold mono-nums text-emerald-900" data-testid="analytics-total-txns">{summary.total_transactions}</p>
                <p className={`text-[11px] ${summary.growth_pct >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {summary.growth_pct >= 0 ? '▲' : '▼'} {Math.abs(summary.growth_pct)}% vs prev period
                </p>
              </div>
              {(summary.current_by_currency || []).slice(0, 2).map((c) => (
                <div key={c.currency} className="p-3 rounded-xl bg-slate-50 border border-slate-100">
                  <p className="text-xs text-slate-500">Volume — {c.currency}</p>
                  <p className="text-xl font-bold mono-nums">{c.currency} {(c.volume || 0).toLocaleString()}</p>
                  <p className="text-[11px] text-slate-500">Fees: {c.currency} {(c.fees || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</p>
                </div>
              ))}
            </div>
          )}

          {analytics?.series?.length > 0 ? (
            <div className="h-64 w-full" data-testid="analytics-timeseries-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={analytics.series} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="bucket" tick={{ fontSize: 11, fill: '#64748b' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
                  <Tooltip
                    contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}
                    formatter={(value, name) => [Number(value).toLocaleString(), name]}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="volume" name="Volume" stroke="#047857" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="fees" name="Fees" stroke="#0891b2" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-sm text-slate-400 text-center py-8" data-testid="analytics-empty-state">No successful transactions in this range yet.</p>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {pieData.length > 0 && (
          <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl" data-testid="analytics-currency-pie-card">
            <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Outfit' }}>Revenue by Currency</CardTitle></CardHeader>
            <CardContent>
              <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={(entry) => `${entry.name} (${Math.round(entry.percent * 100)}%)`}>
                      {pieData.map((entry, idx) => <Cell key={idx} fill={entry.color} />)}
                    </Pie>
                    <Tooltip formatter={(v, n) => [Number(v).toLocaleString(), n]} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}
        {byCurrency?.by_currency?.length > 0 && (
          <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl" data-testid="analytics-currency-bar-card">
            <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Outfit' }}>Transaction Count per Currency</CardTitle></CardHeader>
            <CardContent>
              <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={byCurrency.by_currency} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="currency" tick={{ fontSize: 11, fill: '#64748b' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
                    <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                    <Bar dataKey="count" name="Transactions" fill="#047857" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function UsersTab() {
  const [data, setData] = useState({ users: [], total: 0 });
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  const [actionUser, setActionUser] = useState(null);
  const [accountType, setAccountType] = useState('individual');
  const [accountStatus, setAccountStatus] = useState('pending');
  const [verificationStatus, setVerificationStatus] = useState('not_submitted');
  const [paymentLinkAccess, setPaymentLinkAccess] = useState(true);
  const [apiAccess, setApiAccess] = useState(false);
  const [liveMode, setLiveMode] = useState(false);
  const [withdrawalsEnabled, setWithdrawalsEnabled] = useState(false);
  const [settlementMode, setSettlementMode] = useState('wallet');
  const [settlementStatus, setSettlementStatus] = useState('pending');
  const [flutterwaveSplitEnabled, setFlutterwaveSplitEnabled] = useState(false);
  const [flutterwaveSubaccountId, setFlutterwaveSubaccountId] = useState('');
  const [flutterwaveSubaccountStatus, setFlutterwaveSubaccountStatus] = useState('missing');
  const [settlementCountry, setSettlementCountry] = useState('');
  const [settlementCountryCode, setSettlementCountryCode] = useState('');
  const [settlementCurrency, setSettlementCurrency] = useState('');
  const [settlementSubaccountName, setSettlementSubaccountName] = useState('');
  const [settlementSubaccountEmail, setSettlementSubaccountEmail] = useState('');
  const [settlementAccountType, setSettlementAccountType] = useState('bank_account');
  const [settlementBankCode, setSettlementBankCode] = useState('');
  const [settlementBankName, setSettlementBankName] = useState('');
  const [settlementAccountNumber, setSettlementAccountNumber] = useState('');
  const [settlementAccountName, setSettlementAccountName] = useState('');
  const [settlementBankBranch, setSettlementBankBranch] = useState('');
  const [settlementBranchCode, setSettlementBranchCode] = useState('');
  const [settlementPhoneNumber, setSettlementPhoneNumber] = useState('');
  const [settlementMobileNetwork, setSettlementMobileNetwork] = useState('');
  const [settlementSwiftCode, setSettlementSwiftCode] = useState('');
  const [splitType, setSplitType] = useState('percentage');
  const [jjumaSharePercentage, setJjumaSharePercentage] = useState(5);
  const [subaccountSharePercentage, setSubaccountSharePercentage] = useState(95);
  const [splitValue, setSplitValue] = useState(0);
  const [platformCommissionPercentage, setPlatformCommissionPercentage] = useState(0);
  const [platformCommissionFlat, setPlatformCommissionFlat] = useState(0);
  const [payoutProvider, setPayoutProvider] = useState('flutterwave');
  const [autoWithdrawEnabled, setAutoWithdrawEnabled] = useState(false);
  const [manualWithdrawEnabled, setManualWithdrawEnabled] = useState(true);
  const [autoCreateSubaccountEnabled, setAutoCreateSubaccountEnabled] = useState(false);
  const [manualSubaccountIdAllowed, setManualSubaccountIdAllowed] = useState(false);
  const [withdrawStatus, setWithdrawStatus] = useState('active');
  const [withdrawNotes, setWithdrawNotes] = useState('');
  const [settlementSubaccountResponse, setSettlementSubaccountResponse] = useState({});
  const [settlementSubaccountError, setSettlementSubaccountError] = useState('');
  const [settlementActionLoading, setSettlementActionLoading] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [restoreConfirmOpen, setRestoreConfirmOpen] = useState(false);
  const [deletingUser, setDeletingUser] = useState(false);
  const [restoringUser, setRestoringUser] = useState(false);

  const fetchUsers = useCallback(() => {
    const params = new URLSearchParams();
    if (statusFilter === 'deleted') {
      params.set('status', 'deleted');
    } else if (roleFilter !== 'all') {
      params.set('role', roleFilter);
    }
    api.get(`/admin/users${params}`).then(r => setData(r.data)).catch(() => {});
  }, [roleFilter, statusFilter]);
  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const loadSettlementDetails = async (userId) => {
    try {
      const { data } = await api.get(`/admin/users/${userId}/settlement`);
      const settlement = data?.user || {};
      setActionUser((prev) => ({ ...(prev || {}), ...settlement }));
      setSettlementSubaccountResponse(settlement.flutterwave_subaccount_response || data?.subaccount || {});
      setSettlementSubaccountError(settlement.flutterwave_subaccount_error || data?.subaccount_error || '');
      setSettlementCountry(settlement.settlement_country || settlement.country || '');
      setSettlementCountryCode(settlement.country_code || settlement.settlement_country || settlement.country || '');
      setSettlementCurrency(settlement.currency || settlement.default_currency || 'UGX');
      setSettlementSubaccountName(settlement.subaccount_name || settlement.business_name || settlement.name || '');
      setSettlementSubaccountEmail(settlement.subaccount_email || settlement.email || '');
      setSettlementAccountType(settlement.settlement_account_type || settlement.account_type || 'bank_account');
      setSettlementBankCode(settlement.bank_code || settlement.settlement_bank_code || '');
      setSettlementBankName(settlement.bank_name || settlement.settlement_bank_name || '');
      setSettlementAccountNumber(settlement.account_number || settlement.settlement_account_number || '');
      setSettlementAccountName(settlement.account_name || settlement.settlement_account_name || '');
      setSettlementBankBranch(settlement.bank_branch || settlement.settlement_bank_branch || '');
      setSettlementBranchCode(settlement.branch_code || '');
      setSettlementPhoneNumber(settlement.settlement_phone_number || settlement.phone || '');
      setSettlementMobileNetwork(settlement.settlement_mobile_network || '');
      setSettlementSwiftCode(settlement.swift_code || '');
      setSettlementStatus(settlement.settlement_status || 'pending');
      setSplitType(settlement.split_type || 'percentage');
      setJjumaSharePercentage(Number(settlement.jjuma_share_percentage ?? 5));
      setSubaccountSharePercentage(Number(settlement.subaccount_share_percentage ?? 95));
      setSplitValue(Number(settlement.split_value ?? 0));
      setPlatformCommissionPercentage(Number(settlement.platform_commission_percentage ?? 0));
      setPlatformCommissionFlat(Number(settlement.platform_commission_flat ?? 0));
      setPayoutProvider(settlement.payout_provider || 'flutterwave');
      setAutoWithdrawEnabled(!!settlement.auto_withdraw_enabled);
      setManualWithdrawEnabled(settlement.manual_withdraw_enabled ?? true);
      setAutoCreateSubaccountEnabled(!!settlement.auto_create_subaccount_enabled);
      setManualSubaccountIdAllowed(!!settlement.manual_subaccount_id_allowed);
      setWithdrawStatus(settlement.withdraw_status || 'active');
      setWithdrawNotes(settlement.withdraw_notes || '');
      setFlutterwaveSplitEnabled(!!settlement.flutterwave_split_enabled);
      setFlutterwaveSubaccountId(settlement.flutterwave_subaccount_id || '');
      setFlutterwaveSubaccountStatus(settlement.flutterwave_subaccount_status || 'missing');
    } catch (err) {
      toast.error(formatAdminApiError(err, 'Failed to load settlement details'));
    }
  };

  const updateRole = async () => {
    try {
      await api.patch(`/admin/users/${actionUser._id}`, {
        account_type: accountType,
        account_status: accountStatus,
        verification_status: verificationStatus,
        payment_link_access_enabled: paymentLinkAccess,
        api_access_enabled: apiAccess,
        live_mode_enabled: liveMode,
        withdrawals_enabled: withdrawalsEnabled,
        settlement_mode: settlementMode,
        settlement_status: settlementStatus,
        flutterwave_split_enabled: flutterwaveSplitEnabled,
        auto_create_subaccount_enabled: autoCreateSubaccountEnabled,
        manual_subaccount_id_allowed: manualSubaccountIdAllowed,
        flutterwave_subaccount_id: flutterwaveSubaccountId,
        flutterwave_subaccount_status: flutterwaveSubaccountStatus,
        subaccount_name: settlementSubaccountName,
        subaccount_email: settlementSubaccountEmail,
        country_code: settlementCountryCode,
        currency: settlementCurrency,
        settlement_account_type: settlementAccountType,
        settlement_country: settlementCountry,
        settlement_bank_code: settlementBankCode,
        settlement_bank_name: settlementBankName,
        settlement_account_number: settlementAccountNumber,
        settlement_account_name: settlementAccountName,
        settlement_bank_branch: settlementBankBranch,
        branch_code: settlementBranchCode,
        settlement_phone_number: settlementPhoneNumber,
        settlement_mobile_network: settlementMobileNetwork,
        swift_code: settlementSwiftCode,
        split_type: splitType,
        jjuma_share_percentage: jjumaSharePercentage,
        subaccount_share_percentage: subaccountSharePercentage,
        split_value: splitValue,
        platform_commission_percentage: platformCommissionPercentage,
        platform_commission_flat: platformCommissionFlat,
        payout_provider: payoutProvider,
        auto_withdraw_enabled: autoWithdrawEnabled,
        manual_withdraw_enabled: manualWithdrawEnabled,
        withdraw_status: withdrawStatus,
        withdraw_notes: withdrawNotes,
      });
      toast.success('User updated');
      setActionUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const deleteUser = async () => {
    if (!actionUser) return;
    setDeletingUser(true);
    try {
      await api.delete(`/admin/users/${actionUser._id}`);
      toast.success('User deleted successfully');
      setDeleteConfirmOpen(false);
      setActionUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeletingUser(false);
    }
  };

  const restoreUser = async () => {
    if (!actionUser) return;
    setRestoringUser(true);
    try {
      await api.post(`/admin/users/${actionUser._id}/restore`);
      toast.success('User restored successfully');
      setRestoreConfirmOpen(false);
      setActionUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to restore user');
    } finally {
      setRestoringUser(false);
    }
  };

  const restoredCount = statusFilter === 'deleted'
    ? data.users.filter(u => u.restored_at).length
    : 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setRoleFilter('all'); }}>
          <SelectTrigger className="w-full sm:w-44" data-testid="admin-user-status-filter"><SelectValue placeholder="User status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="active">All Users</SelectItem>
            <SelectItem value="deleted">Deleted Users</SelectItem>
          </SelectContent>
        </Select>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-full sm:w-40" data-testid="admin-user-filter"><SelectValue placeholder="Filter by role" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Users</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="verified">Verified</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-slate-500">
          {statusFilter === 'deleted'
            ? `${data.total} deleted, ${restoredCount} restored`
            : `${data.total} users`}
        </span>
        {statusFilter === 'deleted' && (
          <span className="text-sm text-slate-500">Showing deleted accounts only</span>
        )}
      </div>
      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardContent className="p-0 overflow-x-auto">
          <Table className="min-w-[900px]">
            <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Name</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Email</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Country</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Account Type</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Verification Status</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Role</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Joined</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
              {data.users.map(u => (
                <TableRow key={u._id} className="hover:bg-slate-50/50" data-testid={`admin-user-row-${u._id}`}>
                  <TableCell className="text-sm font-medium text-slate-900">{u.name}</TableCell>
                  <TableCell className="text-sm text-slate-500">{u.email}</TableCell>
                  <TableCell className="text-sm text-slate-500">{u.country}</TableCell>
                  <TableCell className="text-sm capitalize text-slate-700">{u.account_type || 'individual'}</TableCell>
                  <TableCell className="space-y-1">
                    <StatusBadge status={getVerificationLabel(u)} />
                  </TableCell>
                  <TableCell className="space-y-1">
                    <StatusBadge status={getUserRoleLabel(u).toLowerCase().replace(/\s+/g, '_')} />
                    {((u.account_status || '').toLowerCase() === 'deleted' || u.deleted_at) && (
                      <div className="flex flex-wrap items-center gap-1 pt-1">
                        <SmallBadge className="bg-red-100 text-red-800">Deleted</SmallBadge>
                        {u.deleted_at && (
                          <span className="text-[10px] text-slate-400">
                            {new Date(u.deleted_at).toLocaleDateString()}
                          </span>
                        )}
                        {u.deleted_by_email && (
                          <span className="text-[10px] text-slate-400">
                            by {u.deleted_by_email}
                          </span>
                        )}
                      </div>
                    )}
                    {u.restored_at && (
                      <div className="flex flex-wrap items-center gap-1 pt-1">
                        <SmallBadge className="bg-emerald-100 text-emerald-800">Restored</SmallBadge>
                        <span className="text-[10px] text-slate-400">
                          {new Date(u.restored_at).toLocaleDateString()}
                        </span>
                        {u.restored_by_email && (
                          <span className="text-[10px] text-slate-400">
                            by {u.restored_by_email}
                          </span>
                        )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">{new Date(u.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                  <Button variant="ghost" size="sm" className="w-full sm:w-auto" onClick={() => {
                    setActionUser(u);
                    setAccountType(u.account_type || 'individual');
                    setVerificationStatus(getVerificationLabel(u));
                    setAccountStatus(u.account_status || (u.role === 'verified' ? 'approved' : u.role || 'pending'));
                    setPaymentLinkAccess(u.payment_link_access_enabled ?? true);
                    setApiAccess(u.api_access_enabled ?? false);
                    setLiveMode(u.live_mode_enabled ?? false);
                    setWithdrawalsEnabled(u.withdrawals_enabled ?? false);
      setSettlementMode((u.settlement_mode || 'wallet').toLowerCase());
      setSettlementStatus(u.settlement_status || 'pending');
                    loadSettlementDetails(u._id);
                  }} data-testid={`edit-user-${u._id}`}>Edit</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <Dialog open={!!actionUser} onOpenChange={v => { if (!v) setActionUser(null); }}>
        <DialogContent className="w-[calc(100vw-1rem)] sm:w-full max-w-sm max-h-[90vh] overflow-y-auto overflow-x-hidden !p-4 sm:!p-6">
          <DialogHeader className="text-left"><DialogTitle>Update User: {actionUser?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Role</Label>
              <div className="mt-1 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                <StatusBadge status={getUserRoleLabel(actionUser).toLowerCase().replace(/\s+/g, '_')} />
              </div>
            </div>
            <div>
              <Label>Account Type</Label>
              <Select value={accountType} onValueChange={setAccountType}>
                <SelectTrigger className="mt-1" data-testid="admin-account-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="individual">Individual</SelectItem>
                  <SelectItem value="business">Business</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Verification Status</Label>
              <Select value={verificationStatus} onValueChange={setVerificationStatus}>
                <SelectTrigger className="mt-1" data-testid="admin-account-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="not_submitted">Not Submitted</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="verified">Verified</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="more_info_required">More Info Required</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Account Approval</Label>
              <Select value={accountStatus} onValueChange={setAccountStatus}>
                <SelectTrigger className="mt-1" data-testid="admin-account-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Payment Link Access</Label>
              <Select value={paymentLinkAccess ? 'yes' : 'no'} onValueChange={v => setPaymentLinkAccess(v === 'yes')}>
                <SelectTrigger className="mt-1" data-testid="admin-payment-link-access-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Enabled</SelectItem>
                  <SelectItem value="no">Disabled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>API Access</Label>
              <Select value={apiAccess ? 'yes' : 'no'} onValueChange={v => setApiAccess(v === 'yes')}>
                <SelectTrigger className="mt-1" data-testid="admin-api-access-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Enabled</SelectItem>
                  <SelectItem value="no">Disabled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Live Mode</Label>
              <Select value={liveMode ? 'yes' : 'no'} onValueChange={v => setLiveMode(v === 'yes')}>
                <SelectTrigger className="mt-1" data-testid="admin-live-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Enabled</SelectItem>
                  <SelectItem value="no">Disabled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Withdrawals</Label>
              <Select value={withdrawalsEnabled ? 'yes' : 'no'} onValueChange={v => setWithdrawalsEnabled(v === 'yes')}>
                <SelectTrigger className="mt-1" data-testid="admin-withdrawals-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Enabled</SelectItem>
                  <SelectItem value="no">Disabled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-3">
              <div>
                <p className="text-sm font-semibold text-slate-900">Settlement</p>
                <p className="text-xs text-slate-500 mt-0.5">Configure Flutterwave split settlement, wallet fallback, and payout defaults.</p>
              </div>
              <div>
                <Label>Settlement Mode</Label>
                <Select value={settlementMode} onValueChange={setSettlementMode}>
                  <SelectTrigger className="mt-1" data-testid="admin-settlement-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="split">Split Payment</SelectItem>
                    <SelectItem value="wallet">JJuma Wallet</SelectItem>
                    <SelectItem value="manual">Manual Review</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Settlement Status</Label>
                <Select value={settlementStatus} onValueChange={setSettlementStatus}>
                  <SelectTrigger className="mt-1" data-testid="admin-settlement-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="subaccount_created">Subaccount Created</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={flutterwaveSplitEnabled} onChange={e => setFlutterwaveSplitEnabled(e.target.checked)} className="w-4 h-4" data-testid="admin-fw-split-enabled" />
                <span className="text-sm text-slate-700">Enable Flutterwave split payment</span>
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Subaccount Name</Label>
                  <Input value={settlementSubaccountName} onChange={e => setSettlementSubaccountName(e.target.value)} className="mt-1" data-testid="admin-settlement-subaccount-name" />
                </div>
                <div>
                  <Label>Subaccount Email</Label>
                  <Input value={settlementSubaccountEmail} onChange={e => setSettlementSubaccountEmail(e.target.value)} className="mt-1" data-testid="admin-settlement-subaccount-email" />
                </div>
                <div>
                  <Label>Subaccount ID</Label>
                  <Input value={flutterwaveSubaccountId} onChange={e => setFlutterwaveSubaccountId(e.target.value)} placeholder="FLWSECK_SUBACCOUNT..." className="mt-1" data-testid="admin-fw-subaccount-id" />
                </div>
                <div>
                  <Label>Subaccount Status</Label>
                  <Select value={flutterwaveSubaccountStatus} onValueChange={setFlutterwaveSubaccountStatus}>
                    <SelectTrigger className="mt-1" data-testid="admin-fw-subaccount-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="missing">Missing</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="disabled">Disabled</SelectItem>
                      <SelectItem value="failed">Failed</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Settlement Country</Label>
                  <Input value={settlementCountry} onChange={e => setSettlementCountry(e.target.value)} className="mt-1" data-testid="admin-settlement-country" />
                </div>
                <div>
                  <Label>Country Code</Label>
                  <Input value={settlementCountryCode} onChange={e => setSettlementCountryCode(e.target.value.toUpperCase())} className="mt-1" data-testid="admin-country-code" />
                </div>
                <div>
                  <Label>Currency</Label>
                  <Input value={settlementCurrency} onChange={e => setSettlementCurrency(e.target.value.toUpperCase())} className="mt-1" data-testid="admin-settlement-currency" />
                </div>
                <div>
                  <Label>Account Type</Label>
                  <Select value={settlementAccountType} onValueChange={setSettlementAccountType}>
                    <SelectTrigger className="mt-1" data-testid="admin-account-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bank_account">Bank Account</SelectItem>
                      <SelectItem value="mobile_money">Mobile Money</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Bank Code</Label>
                  <Input value={settlementBankCode} onChange={e => setSettlementBankCode(e.target.value)} className="mt-1" data-testid="admin-bank-code" />
                </div>
                <div>
                  <Label>Bank Name</Label>
                  <Input value={settlementBankName} onChange={e => setSettlementBankName(e.target.value)} className="mt-1" data-testid="admin-bank-name" />
                </div>
                <div>
                  <Label>Account Number</Label>
                  <Input value={settlementAccountNumber} onChange={e => setSettlementAccountNumber(e.target.value)} className="mt-1" data-testid="admin-account-number" />
                </div>
                <div>
                  <Label>Account Name</Label>
                  <Input value={settlementAccountName} onChange={e => setSettlementAccountName(e.target.value)} className="mt-1" data-testid="admin-account-name" />
                </div>
                <div>
                  <Label>Bank Branch</Label>
                  <Input value={settlementBankBranch} onChange={e => setSettlementBankBranch(e.target.value)} className="mt-1" data-testid="admin-bank-branch" />
                </div>
                <div>
                  <Label>Branch Code</Label>
                  <Input value={settlementBranchCode} onChange={e => setSettlementBranchCode(e.target.value)} className="mt-1" data-testid="admin-branch-code" />
                </div>
                <div>
                  <Label>Phone Number</Label>
                  <Input value={settlementPhoneNumber} onChange={e => setSettlementPhoneNumber(e.target.value)} className="mt-1" data-testid="admin-settlement-phone" />
                </div>
                <div>
                  <Label>Mobile Network</Label>
                  <Input value={settlementMobileNetwork} onChange={e => setSettlementMobileNetwork(e.target.value)} className="mt-1" data-testid="admin-mobile-network" />
                </div>
                <div>
                  <Label>Swift Code</Label>
                  <Input value={settlementSwiftCode} onChange={e => setSettlementSwiftCode(e.target.value)} className="mt-1" data-testid="admin-swift-code" />
                </div>
                <div>
                  <Label>Split Type</Label>
                  <Select value={splitType} onValueChange={setSplitType}>
                    <SelectTrigger className="mt-1" data-testid="admin-split-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Percentage</SelectItem>
                      <SelectItem value="flat">Flat</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Split Value</Label>
                  <Input type="number" value={splitValue} onChange={e => setSplitValue(parseFloat(e.target.value) || 0)} className="mt-1" data-testid="admin-split-value" />
                </div>
                <div>
                  <Label>JJuma Share %</Label>
                  <Input
                    type="number"
                    value={jjumaSharePercentage}
                    onChange={e => {
                      const next = parseFloat(e.target.value) || 0;
                      setJjumaSharePercentage(next);
                      const merchantShare = Math.max(0, 100 - next);
                      setSubaccountSharePercentage(merchantShare);
                      setSplitValue(Number((merchantShare / 100).toFixed(6)));
                    }}
                    className="mt-1"
                    data-testid="admin-jjuma-share"
                  />
                </div>
                <div>
                  <Label>Merchant Share %</Label>
                  <Input type="number" value={subaccountSharePercentage} readOnly className="mt-1 bg-slate-50" data-testid="admin-merchant-share" />
                </div>
                <div>
                  <Label>Platform Commission %</Label>
                  <Input type="number" value={platformCommissionPercentage} onChange={e => setPlatformCommissionPercentage(parseFloat(e.target.value) || 0)} className="mt-1" data-testid="admin-platform-commission-pct" />
                </div>
                <div>
                  <Label>Platform Commission Flat</Label>
                  <Input type="number" value={platformCommissionFlat} onChange={e => setPlatformCommissionFlat(parseFloat(e.target.value) || 0)} className="mt-1" data-testid="admin-platform-commission-flat" />
                </div>
                <div>
                  <Label>Payout Provider</Label>
                  <Select value={payoutProvider} onValueChange={setPayoutProvider}>
                    <SelectTrigger className="mt-1" data-testid="admin-payout-provider"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="flutterwave">Flutterwave</SelectItem>
                      <SelectItem value="yopayments">Yo! Payments</SelectItem>
                      <SelectItem value="manual">Manual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Withdraw Status</Label>
                  <Select value={withdrawStatus} onValueChange={setWithdrawStatus}>
                    <SelectTrigger className="mt-1" data-testid="admin-withdraw-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="disabled">Disabled</SelectItem>
                      <SelectItem value="review">Review</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={autoWithdrawEnabled} onChange={e => setAutoWithdrawEnabled(e.target.checked)} className="w-4 h-4" data-testid="admin-auto-withdraw" />
                <span className="text-sm text-slate-700">Enable automatic withdraw</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={manualWithdrawEnabled} onChange={e => setManualWithdrawEnabled(e.target.checked)} className="w-4 h-4" data-testid="admin-manual-withdraw" />
                <span className="text-sm text-slate-700">Enable manual withdraw</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={autoCreateSubaccountEnabled} onChange={e => setAutoCreateSubaccountEnabled(e.target.checked)} className="w-4 h-4" data-testid="admin-auto-create-subaccount" />
                <span className="text-sm text-slate-700">Auto create Flutterwave subaccount</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={manualSubaccountIdAllowed} onChange={e => setManualSubaccountIdAllowed(e.target.checked)} className="w-4 h-4" data-testid="admin-manual-subaccount-id" />
                <span className="text-sm text-slate-700">Allow manual subaccount ID entry</span>
              </label>
              <div>
                <Label>Withdraw Notes</Label>
                <Input value={withdrawNotes} onChange={e => setWithdrawNotes(e.target.value)} className="mt-1" data-testid="admin-withdraw-notes" />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                  onClick={async () => {
                    if (!actionUser) return;
                    setSettlementActionLoading(true);
                    try {
                      const { data } = await api.patch(`/admin/users/${actionUser._id}/settlement`, {
                        action: 'create_subaccount',
                      });
                      toast.success(data?.message || 'Flutterwave subaccount created');
                      loadSettlementDetails(actionUser._id);
                      fetchUsers();
                    } catch (err) {
                      toast.error(formatAdminApiError(err, 'Flutterwave subaccount creation failed'));
                    } finally {
                      setSettlementActionLoading(false);
                    }
                  }}
                  disabled={settlementActionLoading}
                  data-testid="admin-create-subaccount-btn"
                >
                  Auto Create Subaccount
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                  onClick={async () => {
                    if (!actionUser) return;
                    setSettlementActionLoading(true);
                    try {
                      const { data } = await api.patch(`/admin/users/${actionUser._id}/settlement`, {
                        action: 'retry_subaccount',
                      });
                      toast.success(data?.message || 'Flutterwave subaccount retried');
                      loadSettlementDetails(actionUser._id);
                      fetchUsers();
                    } catch (err) {
                      toast.error(formatAdminApiError(err, 'Flutterwave subaccount retry failed'));
                    } finally {
                      setSettlementActionLoading(false);
                    }
                  }}
                  disabled={settlementActionLoading}
                  data-testid="admin-retry-subaccount-btn"
                >
                  Retry Subaccount
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="border-red-200 text-red-700 hover:bg-red-50"
                  onClick={async () => {
                    if (!actionUser) return;
                    setSettlementActionLoading(true);
                    try {
                      const { data } = await api.patch(`/admin/users/${actionUser._id}/settlement`, {
                        action: 'remove_subaccount_id',
                      });
                      toast.success(data?.message || 'Subaccount removed');
                      loadSettlementDetails(actionUser._id);
                      fetchUsers();
                    } catch (err) {
                      toast.error(formatAdminApiError(err, 'Failed to remove subaccount ID'));
                    } finally {
                      setSettlementActionLoading(false);
                    }
                  }}
                  disabled={settlementActionLoading}
                  data-testid="admin-remove-subaccount-btn"
                >
                  Remove Subaccount ID
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="border-blue-200 text-blue-700 hover:bg-blue-50"
                  onClick={async () => {
                    if (!actionUser || !flutterwaveSubaccountId) return;
                    setSettlementActionLoading(true);
                    try {
                      const { data } = await api.patch(`/admin/users/${actionUser._id}/settlement`, {
                        action: 'manual_set_subaccount_id',
                        flutterwave_subaccount_id: flutterwaveSubaccountId,
                      });
                      toast.success(data?.message || 'Subaccount ID saved');
                      loadSettlementDetails(actionUser._id);
                      fetchUsers();
                    } catch (err) {
                      toast.error(formatAdminApiError(err, 'Failed to save subaccount ID'));
                    } finally {
                      setSettlementActionLoading(false);
                    }
                  }}
                  disabled={settlementActionLoading || !flutterwaveSubaccountId}
                  data-testid="admin-save-subaccount-btn"
                >
                  Save Subaccount ID
                </Button>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-3 text-xs text-slate-600 space-y-1">
                <p><span className="font-medium text-slate-900">Settlement Status:</span> {settlementStatus || 'pending'}</p>
                <p><span className="font-medium text-slate-900">Subaccount Status:</span> {flutterwaveSubaccountStatus || 'missing'}</p>
                <p><span className="font-medium text-slate-900">Current Payout Provider:</span> {payoutProvider || 'flutterwave'}</p>
                <p><span className="font-medium text-slate-900">Manual Subaccount Entry:</span> {manualSubaccountIdAllowed ? 'Allowed' : 'Disabled'}</p>
              </div>
              <div>
                <Label>Flutterwave Subaccount Response / Error Log</Label>
                <textarea
                  readOnly
                  value={typeof settlementSubaccountResponse === 'string'
                    ? settlementSubaccountResponse
                    : JSON.stringify(settlementSubaccountResponse || actionUser?.flutterwave_subaccount_response || {}, null, 2)}
                  className="mt-1 w-full min-h-[120px] rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 font-mono"
                  data-testid="admin-subaccount-response-log"
                />
              </div>
              <div>
                <Label>Flutterwave Error Log</Label>
                <textarea
                  readOnly
                  value={settlementSubaccountError || actionUser?.flutterwave_subaccount_error || ''}
                  className="mt-1 w-full min-h-[120px] rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 font-mono"
                  data-testid="admin-subaccount-error-log"
                />
              </div>
            </div>
            <Button onClick={updateRole} className="w-full bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="admin-update-user-btn">Update User</Button>

            {actionUser?.restored_at && (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50/70 p-4 space-y-1">
                <p className="text-sm font-semibold text-emerald-900">Restoration History</p>
                <p className="text-xs text-emerald-700">
                  Restored on {new Date(actionUser.restored_at).toLocaleString()}
                </p>
                {actionUser.restored_by_email && (
                  <p className="text-xs text-emerald-700">
                    Restored by {actionUser.restored_by_email}
                  </p>
                )}
              </div>
            )}

            {((actionUser?.account_status || '').toLowerCase() === 'deleted' || actionUser?.deleted_at || actionUser?.login_disabled) ? (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50/70 p-4 space-y-3">
                <div>
                  <p className="text-sm font-semibold text-emerald-900">Restore Zone</p>
                  <p className="text-xs text-emerald-700 mt-1">
                    This account is currently deleted or deactivated. Restoring it will allow the user to sign in again.
                  </p>
                </div>
                <Button
                  type="button"
                  className="w-full bg-emerald-700 hover:bg-emerald-800 text-white"
                  onClick={() => setRestoreConfirmOpen(true)}
                  data-testid="admin-restore-user-btn"
                >
                  Restore User
                </Button>
              </div>
            ) : (
              <div className="rounded-2xl border border-red-200 bg-red-50/70 p-4 space-y-3">
                <div>
                  <p className="text-sm font-semibold text-red-900">Danger Zone</p>
                  <p className="text-xs text-red-700 mt-1">
                    Deleting this user will deactivate their account and may affect access to Jjuma Pay.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full border-red-300 text-red-700 hover:bg-red-100 hover:text-red-800"
                  onClick={() => setDeleteConfirmOpen(true)}
                  data-testid="admin-delete-user-btn"
                >
                  Delete User
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteConfirmOpen} onOpenChange={v => setDeleteConfirmOpen(v)}>
        <DialogContent className="w-[calc(100vw-1rem)] sm:w-full max-w-sm max-h-[85vh] overflow-y-auto overflow-x-hidden !p-4 sm:!p-6">
          <DialogHeader className="text-left">
            <DialogTitle>Delete User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-700">
              Are you sure you want to delete this user?
              <br />
              This action may remove the user’s access to Jjuma Pay and cannot be easily undone.
            </p>
            <div className="flex flex-col sm:flex-row items-stretch gap-2">
              <Button variant="outline" className="w-full sm:flex-1" onClick={() => setDeleteConfirmOpen(false)} data-testid="cancel-delete-user-btn">
                Cancel
              </Button>
              <Button
                className="w-full sm:flex-1 bg-red-600 hover:bg-red-700 text-white"
                onClick={deleteUser}
                disabled={deletingUser}
                data-testid="confirm-delete-user-btn"
              >
                {deletingUser ? 'Deleting...' : 'Yes, Delete User'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={restoreConfirmOpen} onOpenChange={v => setRestoreConfirmOpen(v)}>
        <DialogContent className="w-[calc(100vw-1rem)] sm:w-full max-w-sm max-h-[85vh] overflow-y-auto overflow-x-hidden !p-4 sm:!p-6">
          <DialogHeader className="text-left">
            <DialogTitle>Restore User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-700">
              Are you sure you want to restore this user?
              <br />
              This action will reactivate the account and allow the user to log in again.
            </p>
            <div className="flex flex-col sm:flex-row items-stretch gap-2">
              <Button variant="outline" className="w-full sm:flex-1" onClick={() => setRestoreConfirmOpen(false)} data-testid="cancel-restore-user-btn">
                Cancel
              </Button>
              <Button
                className="w-full sm:flex-1 bg-emerald-700 hover:bg-emerald-800 text-white"
                onClick={restoreUser}
                disabled={restoringUser}
                data-testid="confirm-restore-user-btn"
              >
                {restoringUser ? 'Restoring...' : 'Yes, Restore User'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function WithdrawalsTab() {
  const { user } = useAuth();
  const [data, setData] = useState({ withdrawals: [], total: 0 });
  const [statusFilter, setStatusFilter] = useState('all');
  const [actionW, setActionW] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [detailW, setDetailW] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [payoutW, setPayoutW] = useState(null);
  const [payoutDetail, setPayoutDetail] = useState(null);
  const [payoutStep, setPayoutStep] = useState('form');
  const [destinationForm, setDestinationForm] = useState({});
  const [otpCode, setOtpCode] = useState('');
  const [processingPayout, setProcessingPayout] = useState(false);
  const [resendingOtp, setResendingOtp] = useState(false);

  const perms = user?.admin_permissions || [];
  const isSuperAdmin = user?.admin_role === 'super_admin';
  const canApproveWithdrawals = isSuperAdmin || perms.includes('manage_withdrawals') || perms.includes('approve_withdrawals') || perms.includes('can_approve_withdrawals');
  const canSendPayouts = isSuperAdmin || perms.includes('can_send_payouts');

  const fetchW = useCallback(() => {
    const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
    api.get(`/admin/withdrawals${params}`).then(r => setData(r.data)).catch(() => {});
  }, [statusFilter]);
  useEffect(() => { fetchW(); }, [fetchW]);

  const submitStatus = async () => {
    try {
      await api.patch(`/admin/withdrawals/${actionW.id}/status`, { status: newStatus, admin_notes: adminNotes });
      toast.success(`Withdrawal marked as ${newStatus}`);
      setActionW(null);
      setAdminNotes('');
      fetchW();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const quickStatus = async (withdrawal, status) => {
    try {
      await api.patch(`/admin/withdrawals/${withdrawal.id}/status`, {
        status,
        admin_notes: status === 'rejected' ? 'Rejected by admin.' : `Marked as ${status} by admin.`,
      });
      toast.success(`Withdrawal marked as ${status}`);
      fetchW();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const openDetail = async (withdrawal) => {
    setDetailLoading(true);
    try {
      const { data: detail } = await api.get(`/admin/withdrawals/${withdrawal.id}/payout`);
      setDetailW(detail);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load withdrawal details');
    } finally {
      setDetailLoading(false);
    }
  };

  const openPayout = async (withdrawal) => {
    setProcessingPayout(true);
    try {
      const { data: detail } = await api.get(`/admin/withdrawals/${withdrawal.id}/payout`);
      setPayoutDetail(detail);
      setPayoutW(detail.withdrawal || withdrawal);
      const destination = detail.destination || {};
      setDestinationForm({
        withdrawal_method: detail.withdrawal?.withdrawal_method || withdrawal.withdrawal_method || 'mobile_money',
        country: destination.country || withdrawal.country || '',
        recipient_name: destination.recipient_name || destination.account_name || withdrawal.account_name || '',
        phone_number: destination.phone_number || destination.mobile_money_number || withdrawal.mobile_money_number || '',
        network: destination.network || '',
        bank_name: destination.bank_name || withdrawal.bank_name || '',
        bank_code: destination.bank_code || '',
        account_name: destination.account_name || withdrawal.account_name || '',
        account_number: destination.account_number || withdrawal.account_number || '',
      });
      setPayoutStep('form');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load payout details');
    } finally {
      setProcessingPayout(false);
    }
  };

  const initiatePayout = async () => {
    if (!payoutW) return;
    setProcessingPayout(true);
    try {
      const { data } = await api.post(`/admin/withdrawals/${payoutW.id}/payout/initiate`, destinationForm);
      if (data?.payout_status === 'otp_pending') {
        setPayoutDetail(prev => (prev ? { ...prev, ...data } : prev));
        setPayoutStep('otp');
        toast.success('OTP sent to admin email');
      } else {
        toast.success(data?.message || 'Payout locked');
        fetchW();
        setPayoutW(null);
        setPayoutDetail(null);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to initiate payout');
    } finally {
      setProcessingPayout(false);
    }
  };

  const confirmPayout = async () => {
    if (!payoutW) return;
    setProcessingPayout(true);
    try {
      const { data } = await api.post(`/admin/withdrawals/${payoutW.id}/payout/confirm-otp`, {
        otp: otpCode,
        destination: destinationForm,
      });
      toast.success(data?.message || 'Payout sent');
      fetchW();
      setPayoutW(null);
      setPayoutDetail(null);
      setOtpCode('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send payout');
    } finally {
      setProcessingPayout(false);
    }
  };

  const resendPayoutOtp = async () => {
    if (!payoutW) return;
    setResendingOtp(true);
    try {
      await api.post(`/admin/withdrawals/${payoutW.id}/payout/resend-otp`);
      toast.success('OTP resent');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend OTP');
    } finally {
      setResendingOtp(false);
    }
  };

  const verifyPayout = async () => {
    if (!payoutW) return;
    setProcessingPayout(true);
    try {
      const { data } = await api.post(`/admin/withdrawals/${payoutW.id}/payout/check`);
      toast.success(`Payout status: ${data.status}`);
      fetchW();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to verify payout');
    } finally {
      setProcessingPayout(false);
    }
  };

  return (
    <div className="space-y-4">
      <Select value={statusFilter} onValueChange={setStatusFilter}>
        <SelectTrigger className="w-full sm:w-44" data-testid="admin-withdrawal-filter"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Statuses</SelectItem>
          <SelectItem value="pending">Pending</SelectItem>
          <SelectItem value="approved">Approved</SelectItem>
          <SelectItem value="processing">Processing</SelectItem>
          <SelectItem value="paid">Paid</SelectItem>
          <SelectItem value="rejected">Rejected</SelectItem>
          <SelectItem value="cancelled">Cancelled</SelectItem>
          <SelectItem value="refunded">Refunded</SelectItem>
        </SelectContent>
      </Select>
      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardContent className="p-0 overflow-x-auto">
          <Table className="min-w-[860px]">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-xs font-semibold text-slate-500 uppercase">Method</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase">Gross</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase">Fee</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase">Net</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase">Currency</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase">Account</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase">Status</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase">Date</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.withdrawals.map(w => (
                <TableRow key={w.id} className="hover:bg-slate-50/50">
                  <TableCell className="text-sm">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${w.withdrawal_method === 'bank_transfer' ? 'bg-blue-100 text-blue-800' : 'bg-emerald-100 text-emerald-800'}`}>
                      {w.withdrawal_method === 'bank_transfer' ? 'Bank Transfer' : 'Mobile Money'}
                    </span>
                  </TableCell>
                  <TableCell className="font-semibold text-sm mono-nums">{w.currency} {Number(w.gross_amount ?? w.amount ?? 0).toLocaleString()}</TableCell>
                  <TableCell className="text-sm text-slate-500 mono-nums">{w.currency} {Number(w.total_fee ?? w.fee_amount ?? w.fee ?? 0).toLocaleString()}</TableCell>
                  <TableCell className="text-sm mono-nums">{w.currency} {Number(w.net_amount ?? 0).toLocaleString()}</TableCell>
                  <TableCell className="text-sm">{w.currency}</TableCell>
                  <TableCell className="text-sm">{w.account_name} - {w.bank_name || w.mobile_money_number}</TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <StatusBadge status={w.status} />
                      <SettlementBadge withdrawal={w} />
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">{new Date(w.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <div className="flex flex-col sm:flex-row sm:flex-wrap gap-1">
                      <Button variant="outline" size="sm" className="w-full sm:w-auto" onClick={() => openDetail(w)} data-testid={`view-w-${w.id}`}>View Details</Button>
                      {canApproveWithdrawals && w.status !== 'paid' && w.status !== 'rejected' && w.status !== 'cancelled' && (
                        <>
                          <Button variant="outline" size="sm" className="w-full sm:w-auto" onClick={() => quickStatus(w, 'approved')} data-testid={`approve-w-${w.id}`}>Approve</Button>
                          <Button variant="outline" size="sm" className="w-full sm:w-auto text-red-600" onClick={() => quickStatus(w, 'rejected')} data-testid={`reject-w-${w.id}`}>Reject</Button>
                        </>
                      )}
                      {canSendPayouts && w.status !== 'paid' && (
                        <Button variant="outline" size="sm" className="w-full sm:w-auto border-emerald-200 text-emerald-700" onClick={() => openPayout(w)} data-testid={`send-payout-${w.id}`} disabled={['successful', 'processing', 'paid'].includes(String(w.payout_status || '').toLowerCase()) || ['paid', 'processing'].includes(String(w.status || '').toLowerCase())}>Send Payout</Button>
                      )}
                      {canApproveWithdrawals && <Button variant="outline" size="sm" className="w-full sm:w-auto" onClick={() => { setActionW(w); setNewStatus(w.status); setAdminNotes(w.admin_notes || ''); }} data-testid={`update-w-${w.id}`}>Update status</Button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <Dialog open={!!actionW} onOpenChange={v => { if (!v) setActionW(null); }}>
        <DialogContent className="w-[calc(100vw-1rem)] sm:w-full max-w-sm max-h-[90vh] overflow-y-auto overflow-x-hidden !p-4 sm:!p-6">
          <DialogHeader><DialogTitle>Update Withdrawal Status</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">{actionW?.currency} {actionW?.amount?.toLocaleString()} • {actionW?.account_name}</p>
            <div>
              <Label>Status</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger className="mt-1" data-testid="w-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['pending','approved','processing','paid','rejected','cancelled','refunded'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Admin notes</Label>
              <Input value={adminNotes} onChange={e => setAdminNotes(e.target.value)} placeholder="Why this status change?" className="mt-1" data-testid="w-admin-notes" />
            </div>
            <Button onClick={submitStatus} disabled={!newStatus} className="w-full bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="w-submit-status-btn">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!detailW} onOpenChange={v => { if (!v) setDetailW(null); }}>
        <DialogContent className="w-[calc(100vw-1rem)] sm:w-full max-w-2xl max-h-[88vh] overflow-y-auto overflow-x-hidden !p-4 sm:!p-6">
          <DialogHeader className="text-left"><DialogTitle>Withdrawal Details</DialogTitle></DialogHeader>
          {detailLoading && <p className="text-sm text-slate-500">Loading…</p>}
          {detailW && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                {[
                  ['User', detailW.user?.name || detailW.withdrawal?.user_id || 'Unknown'],
                  ['Email', detailW.user?.email || '-'],
                  ['Amount', `${detailW.withdrawal?.currency || 'UGX'} ${Number(detailW.withdrawal?.amount || 0).toLocaleString()}`],
                  ['Fee', `${detailW.withdrawal?.currency || 'UGX'} ${Number(detailW.withdrawal?.total_fee || 0).toLocaleString()}`],
                  ['Net', `${detailW.withdrawal?.currency || 'UGX'} ${Number(detailW.withdrawal?.net_amount || 0).toLocaleString()}`],
                  ['Method', (detailW.withdrawal?.withdrawal_method || '').replace('_', ' ')],
                  ['Status', detailW.withdrawal?.status || '-'],
                  ['Payout Status', detailW.withdrawal?.payout_status || 'not_started'],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-xl border border-slate-200 bg-slate-50 p-3 sm:p-4 min-w-0">
                    <p className="text-[11px] uppercase text-slate-500">{label}</p>
                    <p className="font-medium text-slate-900 mt-1 break-words">{value}</p>
                  </div>
                ))}
              </div>
              <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
                <p className="text-xs uppercase text-blue-700 mb-2">Settlement Summary</p>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-800">
                    {WithdrawalSettlementSummary({ withdrawal: detailW.withdrawal })}
                  </span>
                  <span className="text-sm text-blue-900">
                    {detailW.withdrawal?.settled_directly_to_merchant || String(detailW.withdrawal?.settlement_mode || '').toLowerCase() === 'flutterwave_split'
                      ? 'Money was settled directly to the merchant subaccount.'
                      : String(detailW.withdrawal?.settlement_mode || '').toLowerCase() === 'jjuma_wallet'
                        ? 'Money was credited to the JJuma wallet balance.'
                        : 'The payout is still waiting on settlement processing.'}
                  </span>
                </div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <p className="text-xs uppercase text-slate-500 mb-2">Destination</p>
                {Object.keys(detailW.masked_destination || {}).length ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {Object.entries(detailW.masked_destination || {}).map(([key, value]) => (
                      <div key={key} className="rounded-lg border border-slate-200 bg-slate-50 p-3 min-w-0">
                        <p className="text-[11px] uppercase text-slate-500">{key.replace(/_/g, ' ')}</p>
                        <p className="text-sm text-slate-800 mt-1 break-words">{String(value || '-')}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">No destination details saved.</p>
                )}
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <p className="text-xs uppercase text-slate-500 mb-2">Balance Check</p>
                <p className="text-sm text-slate-700">Available: {detailW.balance_check?.currency || 'UGX'} {Number(detailW.balance_check?.available_balance || 0).toLocaleString()}</p>
                <p className="text-sm text-slate-700">Pending: {detailW.balance_check?.currency || 'UGX'} {Number(detailW.balance_check?.pending_balance || 0).toLocaleString()}</p>
                <p className="text-sm text-slate-700">Net amount: {detailW.balance_check?.currency || 'UGX'} {Number(detailW.balance_check?.net_amount || 0).toLocaleString()}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <p className="text-xs uppercase text-slate-500 mb-2">Risk Notes</p>
                <ul className="list-disc ml-5 text-sm text-slate-700 space-y-1">
                  {(detailW.risk_notes || []).map((note, i) => <li key={i}>{note}</li>)}
                </ul>
              </div>
              {(detailW.provider_response || detailW.audit_trail?.length || detailW.payout_response_logs?.length) && (
                <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
                  <div>
                    <p className="text-xs uppercase text-slate-500 mb-2">Payout Logs</p>
                    {detailW.provider_response && Object.keys(detailW.provider_response || {}).length > 0 ? (
                      <pre className="text-[11px] leading-5 text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-words">
                        {JSON.stringify(detailW.provider_response, null, 2)}
                      </pre>
                    ) : (
                      <p className="text-sm text-slate-500">No provider response saved yet.</p>
                    )}
                  </div>
                  {Array.isArray(detailW.payout_response_logs) && detailW.payout_response_logs.length > 0 && (
                    <div>
                      <p className="text-xs uppercase text-slate-500 mb-2">Webhook History</p>
                      <div className="space-y-2">
                        {detailW.payout_response_logs.map((log, idx) => (
                          <div key={log.id || idx} className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
                            <div className="flex items-center justify-between gap-2">
                              <p className="font-medium text-slate-900">{log.provider || 'provider'} • {log.status || 'unknown'}</p>
                              <span className="text-[11px] text-slate-500">{log.created_at ? new Date(log.created_at).toLocaleString() : ''}</span>
                            </div>
                            <p className="text-xs text-slate-500 mt-1">{log.event_type || 'status_update'}</p>
                            {log.error && <p className="text-xs text-red-600 mt-1">{log.error}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {Array.isArray(detailW.audit_trail) && detailW.audit_trail.length > 0 && (
                    <div>
                      <p className="text-xs uppercase text-slate-500 mb-2">Audit Trail</p>
                      <div className="space-y-2">
                        {detailW.audit_trail.map((log, idx) => (
                          <div key={log._id || log.id || idx} className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
                            <div className="flex items-center justify-between gap-2">
                              <p className="font-medium text-slate-900">{log.action || 'audit_event'}</p>
                              <span className="text-[11px] text-slate-500">{log.created_at ? new Date(log.created_at).toLocaleString() : ''}</span>
                            </div>
                            <p className="text-xs text-slate-500 mt-1">{log.admin_notes || log.admin_note || log.error || ''}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {canSendPayouts && (
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button variant="outline" className="w-full sm:flex-1" onClick={verifyPayout} disabled={processingPayout}>Verify Payout</Button>
                  <Button className="w-full sm:flex-1 bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => {
                    setPayoutW(detailW.withdrawal);
                    setPayoutDetail(detailW);
                    setDestinationForm({
                      ...detailW.destination,
                      withdrawal_method: detailW.withdrawal?.withdrawal_method || 'mobile_money',
                    });
                    setPayoutStep('form');
                    setDetailW(null);
                  }} disabled={['successful', 'processing', 'paid'].includes(String(detailW.withdrawal?.payout_status || '').toLowerCase()) || ['paid', 'processing'].includes(String(detailW.withdrawal?.status || '').toLowerCase())}>Send Payout</Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={!!payoutW} onOpenChange={v => { if (!v) { setPayoutW(null); setPayoutDetail(null); setOtpCode(''); setPayoutStep('form'); } }}>
        <DialogContent className="w-[calc(100vw-1rem)] sm:w-full max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden !p-4 sm:!p-6">
          <DialogHeader className="text-left"><DialogTitle>Send Payout</DialogTitle></DialogHeader>
          {payoutW && payoutStep === 'form' && (
            <div className="space-y-4">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                  <div className="min-w-0">
                    <p className="text-[11px] uppercase text-slate-500">User</p>
                    <p className="font-medium text-slate-900 break-words">{payoutDetail?.user?.name || payoutW.user_id || 'Unknown'}</p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] uppercase text-slate-500">Status</p>
                    <p className="font-medium text-slate-900 break-words">{payoutW.status}</p>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase text-slate-500">Amount</p>
                    <p className="font-medium text-slate-900">{payoutW.currency} {Number(payoutW.net_amount || 0).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase text-slate-500">Fee</p>
                    <p className="font-medium text-slate-900">{payoutW.currency} {Number(payoutW.total_fee || payoutW.fee_amount || 0).toLocaleString()}</p>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Method</Label>
                  <Select value={destinationForm.withdrawal_method || 'mobile_money'} onValueChange={v => setDestinationForm(p => ({ ...p, withdrawal_method: v }))}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mobile_money">Mobile Money</SelectItem>
                      <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Country</Label>
                  <Input value={destinationForm.country || ''} onChange={e => setDestinationForm(p => ({ ...p, country: e.target.value }))} className="mt-1" />
                </div>
                {destinationForm.withdrawal_method === 'bank_transfer' ? (
                  <>
                    <div><Label>Bank Name</Label><Input value={destinationForm.bank_name || ''} onChange={e => setDestinationForm(p => ({ ...p, bank_name: e.target.value }))} className="mt-1" /></div>
                    <div><Label>Bank Code</Label><Input value={destinationForm.bank_code || ''} onChange={e => setDestinationForm(p => ({ ...p, bank_code: e.target.value }))} className="mt-1" /></div>
                    <div><Label>Account Name</Label><Input value={destinationForm.account_name || ''} onChange={e => setDestinationForm(p => ({ ...p, account_name: e.target.value }))} className="mt-1" /></div>
                    <div><Label>Account Number</Label><Input value={destinationForm.account_number || ''} onChange={e => setDestinationForm(p => ({ ...p, account_number: e.target.value }))} className="mt-1" /></div>
                  </>
                ) : (
                  <>
                    <div><Label>Phone Number</Label><Input value={destinationForm.phone_number || ''} onChange={e => setDestinationForm(p => ({ ...p, phone_number: e.target.value }))} className="mt-1" /></div>
                    <div><Label>Network</Label><Input value={destinationForm.network || ''} onChange={e => setDestinationForm(p => ({ ...p, network: e.target.value }))} className="mt-1" /></div>
                    <div><Label>Recipient Name</Label><Input value={destinationForm.recipient_name || ''} onChange={e => setDestinationForm(p => ({ ...p, recipient_name: e.target.value }))} className="mt-1" /></div>
                  </>
                )}
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button variant="outline" className="w-full sm:flex-1" onClick={() => { setPayoutW(null); setPayoutDetail(null); }}>Cancel</Button>
                <Button className="w-full sm:flex-1 bg-emerald-700 hover:bg-emerald-800 text-white" onClick={initiatePayout} disabled={processingPayout || payoutW?.payout_status === 'successful'}>
                  {processingPayout ? 'Processing…' : 'Continue'}
                </Button>
              </div>
            </div>
          )}
          {payoutW && payoutStep === 'otp' && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">An OTP was sent to the admin email address before this payout can be processed.</p>
              <div>
                <Label>OTP Code</Label>
                <Input value={otpCode} onChange={e => setOtpCode(e.target.value)} className="mt-1 tracking-[0.25em] sm:tracking-[0.35em] text-center text-lg" maxLength={6} />
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button variant="outline" className="w-full sm:flex-1" onClick={resendPayoutOtp} disabled={resendingOtp}>{resendingOtp ? 'Resending…' : 'Resend OTP'}</Button>
                <Button variant="outline" className="w-full sm:flex-1" onClick={() => setPayoutStep('form')}>Back</Button>
                <Button className="w-full sm:flex-1 bg-emerald-700 hover:bg-emerald-800 text-white" onClick={confirmPayout} disabled={processingPayout || otpCode.length < 4}>{processingPayout ? 'Sending…' : 'Confirm Withdrawal'}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function VerificationsTab() {
  const [data, setData] = useState({ verifications: [], total: 0 });
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchV = useCallback(() => {
    const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
    api.get(`/admin/verifications${params}`).then(r => setData(r.data)).catch(() => {});
  }, [statusFilter]);
  useEffect(() => { fetchV(); }, [fetchV]);

  const updateVerification = async (userId, status) => {
    try {
      await api.patch(`/admin/verifications/${userId}`, { status });
      toast.success(`Verification ${status}`);
      fetchV();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  return (
    <div className="space-y-4">
      <Select value={statusFilter} onValueChange={setStatusFilter}>
        <SelectTrigger className="w-full sm:w-40" data-testid="admin-verification-filter"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All</SelectItem>
          <SelectItem value="pending">Pending</SelectItem>
          <SelectItem value="verified">Verified</SelectItem>
          <SelectItem value="rejected">Rejected</SelectItem>
        </SelectContent>
      </Select>
      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardContent className="p-0 overflow-x-auto">
          <Table className="min-w-[760px]">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-xs font-semibold text-slate-500 uppercase">User</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase">Type</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase">Status</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase">Submitted</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.verifications.map((v, i) => (
                <TableRow key={i} className="hover:bg-slate-50/50">
                  <TableCell>
                    <p className="text-sm font-medium text-slate-900">{v.user?.name || 'Unknown'}</p>
                    <p className="text-xs text-slate-400">{v.user?.email}</p>
                  </TableCell>
                  <TableCell className="text-sm capitalize whitespace-nowrap">{v.type}</TableCell>
                  <TableCell><StatusBadge status={v.status} /></TableCell>
                  <TableCell className="text-sm text-slate-500 whitespace-nowrap">{v.submitted_at ? new Date(v.submitted_at).toLocaleDateString() : '-'}</TableCell>
                  <TableCell className="min-w-max">
                    <div className="flex flex-col sm:flex-row sm:flex-wrap gap-1">
                    {v.status === 'pending' && (
                      <>
                        <Button variant="outline" size="sm" className="w-full sm:w-auto" onClick={() => updateVerification(v.user_id, 'verified')} data-testid={`verify-user-${v.user_id}`}>Verify</Button>
                        <Button variant="outline" size="sm" className="w-full sm:w-auto text-red-600" onClick={() => updateVerification(v.user_id, 'rejected')} data-testid={`reject-user-${v.user_id}`}>Reject</Button>
                      </>
                    )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function AdminTransactionsTab() {
  const [data, setData] = useState({ transactions: [], total: 0, pages: 1 });
  const [statusFilter, setStatusFilter] = useState('all');
  const [currencyFilter, setCurrencyFilter] = useState('all');
  const [providerFilter, setProviderFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState(null);
  const [editStatus, setEditStatus] = useState(null);
  const [statusNote, setStatusNote] = useState('');

  const fetchTxns = useCallback(() => {
    const params = new URLSearchParams({ page, limit: 20 });
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (currencyFilter !== 'all') params.set('currency', currencyFilter);
    if (providerFilter !== 'all') params.set('provider', providerFilter);
    if (sourceFilter !== 'all') params.set('source', sourceFilter);
    api.get(`/admin/transactions?${params}`).then(r => setData(r.data)).catch(() => {});
  }, [page, statusFilter, currencyFilter, providerFilter, sourceFilter]);
  useEffect(() => { fetchTxns(); }, [fetchTxns]);

  const submitStatusChange = async () => {
    try {
      await api.patch(`/admin/transactions/${detail.reference_code}/status`, { status: editStatus, admin_note: statusNote });
      toast.success(`Transaction marked as ${editStatus}`);
      setEditStatus(null); setStatusNote(''); setDetail(null);
      fetchTxns();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-full sm:w-[130px]" data-testid="admin-txn-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="successful">Successful</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
            <SelectItem value="reversed">Reversed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={currencyFilter} onValueChange={v => { setCurrencyFilter(v); setPage(1); }}>
          <SelectTrigger className="w-full sm:w-[120px]" data-testid="admin-txn-currency-filter"><SelectValue placeholder="Currency" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Currency</SelectItem>
            <SelectItem value="UGX">UGX</SelectItem>
            <SelectItem value="USD">USD</SelectItem>
            <SelectItem value="KES">KES</SelectItem>
            <SelectItem value="TZS">TZS</SelectItem>
            <SelectItem value="RWF">RWF</SelectItem>
          </SelectContent>
        </Select>
        <Select value={providerFilter} onValueChange={v => { setProviderFilter(v); setPage(1); }}>
          <SelectTrigger className="w-full sm:w-[130px]" data-testid="admin-txn-provider-filter"><SelectValue placeholder="Gateway" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Gateways</SelectItem>
            <SelectItem value="auto">Simulated</SelectItem>
            <SelectItem value="flutterwave">Flutterwave</SelectItem>
            <SelectItem value="pesapal">Pesapal</SelectItem>
            <SelectItem value="yopayments">Yo! Payments</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sourceFilter} onValueChange={v => { setSourceFilter(v); setPage(1); }}>
          <SelectTrigger className="w-full sm:w-[130px]" data-testid="admin-txn-source-filter"><SelectValue placeholder="Source" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sources</SelectItem>
            <SelectItem value="payment_link">Payment Link</SelectItem>
            <SelectItem value="api">API</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-slate-500 ml-auto">{data.total} transactions</span>
      </div>
      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardContent className="p-0 overflow-x-auto">
          <Table className="min-w-[1200px]">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-xs font-semibold text-slate-500 uppercase">TXN ID</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase">Receiver</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase">Payer</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase">Amount</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase">Fee</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase">Net</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase">Source</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase">Gateway</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase">Status</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase">Date</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.transactions.map((t, i) => (
                <TableRow key={i} className="hover:bg-slate-50/50" data-testid={`admin-txn-row-${i}`}>
                  <TableCell className="text-[11px] mono-nums text-slate-600">{t.transaction_id}</TableCell>
                  <TableCell>
                    <p className="text-sm font-medium text-slate-900 truncate max-w-[120px]">{t.receiver_name || 'Unknown'}</p>
                    <p className="text-[10px] text-slate-400 capitalize">{t.account_type || 'individual'}</p>
                  </TableCell>
                  <TableCell>
                    <p className="text-sm text-slate-700 truncate max-w-[120px]">{t.sender_name}</p>
                    <p className="text-[10px] text-slate-400">{t.sender_email || t.sender_phone}</p>
                  </TableCell>
                  <TableCell className="font-semibold text-sm mono-nums text-emerald-700 whitespace-nowrap">{t.currency} {t.amount?.toLocaleString()}</TableCell>
                  <TableCell className="text-xs text-slate-500 mono-nums whitespace-nowrap">{t.currency} {t.fee?.toLocaleString()}</TableCell>
                  <TableCell className="text-xs mono-nums whitespace-nowrap">{t.currency} {t.net_amount?.toLocaleString()}</TableCell>
                  <TableCell><span className={`text-[10px] px-1.5 py-0.5 rounded-full ${t.source === 'payment_link' ? 'bg-blue-50 text-blue-700' : t.source === 'api' ? 'bg-violet-50 text-violet-700' : 'bg-slate-50 text-slate-600'}`}>{t.source === 'payment_link' ? 'Link' : t.source || 'N/A'}</span></TableCell>
                  <TableCell className="text-xs capitalize">{(t.gateway || t.provider) === 'auto' ? 'simulated' : (t.gateway || t.provider || 'N/A')}</TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <StatusBadge status={t.status} />
                      <TransactionSettlementBadge txn={t} />
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-slate-500 whitespace-nowrap">{new Date(t.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm" className="w-full sm:w-auto text-xs" onClick={() => setDetail(t)} data-testid={`view-txn-${i}`}>View</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      {data.pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1.5 text-sm rounded-lg border bg-white hover:bg-slate-50 disabled:opacity-40">Previous</button>
          <span className="text-sm text-slate-500">Page {page} of {data.pages}</span>
          <button onClick={() => setPage(p => Math.min(data.pages, p + 1))} disabled={page === data.pages} className="px-3 py-1.5 text-sm rounded-lg border bg-white hover:bg-slate-50 disabled:opacity-40">Next</button>
        </div>
      )}
      <Dialog open={!!detail} onOpenChange={v => { if (!v) setDetail(null); }}>
        <DialogContent className="w-[calc(100vw-1rem)] sm:w-full max-w-lg max-h-[85vh] overflow-y-auto overflow-x-hidden !p-4 sm:!p-6">
          <DialogHeader className="text-left"><DialogTitle style={{ fontFamily: 'Outfit' }}>Transaction Details</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                {[
                  ['Transaction ID', detail.transaction_id],
                  ['Reference', detail.reference_code],
                  ['Status', detail.status],
                  ['Amount', `${detail.currency} ${detail.amount?.toLocaleString()}`],
                  ['Fee', `${detail.currency} ${detail.fee?.toLocaleString()}`],
                  ['Net Amount', `${detail.currency} ${detail.net_amount?.toLocaleString()}`],
                  ['Receiver', detail.receiver_name || 'Unknown'],
                  ['Receiver Email', detail.receiver_email || '-'],
                  ['Account Type', detail.account_type || 'individual'],
                  ['Payer', detail.sender_name],
                  ['Payer Phone', detail.sender_phone || '-'],
                  ['Payer Email', detail.sender_email || '-'],
                  ['Source', detail.source || 'N/A'],
                  ['Payment Link', detail.payment_link_slug || '-'],
                  ['Gateway', detail.gateway || detail.provider || 'auto'],
                  ['Gateway Ref', detail.provider_reference || '-'],
                  ['Payment Method', detail.payment_method || '-'],
                  ['Date', new Date(detail.created_at).toLocaleString()],
                ].map(([label, value]) => (
                  <div key={label} className="py-1 min-w-0">
                    <p className="text-xs text-slate-400">{label}</p>
                    <p className="font-medium text-slate-900 capitalize break-words">{value}</p>
                  </div>
                ))}
              </div>
              {detail.message && (
                <div className="pt-2 border-t border-slate-100">
                  <p className="text-xs text-slate-400">Message</p>
                  <p className="text-slate-700">{detail.message}</p>
                </div>
              )}
              <div className="pt-3 border-t border-slate-100 space-y-2">
                <Label className="text-xs">Admin: change transaction status</Label>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Select value={editStatus || detail.status} onValueChange={setEditStatus}>
                    <SelectTrigger className="w-full sm:w-40" data-testid="admin-txn-edit-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['pending','successful','failed','cancelled','refunded','reversed'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input value={statusNote} onChange={e => setStatusNote(e.target.value)} placeholder="Admin note (audit log)" className="flex-1" data-testid="admin-txn-status-note" />
                </div>
                <Button onClick={submitStatusChange} disabled={!editStatus || editStatus === detail.status} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="admin-txn-save-status">
                  Apply status change
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CommissionTiersSection() {
  const [tiers, setTiers] = useState([]);
  const [form, setForm] = useState({ currency: 'UGX', min_amount: '', max_amount: '', percentage_fee: '', fixed_fee: '', provider: 'all', applies_to: 'payment_received', active: true });
  const [adding, setAdding] = useState(false);

  const fetchTiers = () => api.get('/admin/commission-tiers').then(r => setTiers(r.data.tiers || [])).catch(() => {});
  useEffect(() => { fetchTiers(); }, []);

  const u = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const addTier = async () => {
    setAdding(true);
    try {
      const payload = { ...form,
        min_amount: parseFloat(form.min_amount) || 0,
        max_amount: form.max_amount === '' ? null : parseFloat(form.max_amount),
        percentage_fee: parseFloat(form.percentage_fee) || 0,
        fixed_fee: parseFloat(form.fixed_fee) || 0,
      };
      await api.post('/admin/commission-tiers', payload);
      toast.success('Tier added');
      setForm({ currency: 'UGX', min_amount: '', max_amount: '', percentage_fee: '', fixed_fee: '', provider: 'all', applies_to: 'payment_received', active: true });
      fetchTiers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setAdding(false); }
  };

  const toggleActive = async (t) => {
    try { await api.patch(`/admin/commission-tiers/${t.id}`, { active: !t.active }); fetchTiers(); }
    catch { toast.error('Failed'); }
  };

  const deleteTier = async (id) => {
    if (!window.confirm('Delete this tier?')) return;
    try { await api.delete(`/admin/commission-tiers/${id}`); toast.success('Tier deleted'); fetchTiers(); }
    catch { toast.error('Failed'); }
  };

  return (
    <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl" data-testid="commission-tiers-card">
      <CardHeader>
        <CardTitle style={{ fontFamily: 'Outfit' }}>Commission Tiers by Amount</CardTitle>
        <p className="text-xs text-slate-500 mt-1">Add unlimited tiers. The first matching tier (by currency, gateway, applies_to and amount range) is used. If no tier matches, the per-currency commission applies.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {tiers.length > 0 && (
          <div className="overflow-x-auto">
            <Table className="min-w-[900px]">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs">Currency</TableHead>
                  <TableHead className="text-xs">Min</TableHead>
                  <TableHead className="text-xs">Max</TableHead>
                  <TableHead className="text-xs">%</TableHead>
                  <TableHead className="text-xs">Fixed</TableHead>
                  <TableHead className="text-xs">Gateway</TableHead>
                  <TableHead className="text-xs">Applies to</TableHead>
                  <TableHead className="text-xs">Status</TableHead>
                  <TableHead className="text-xs">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tiers.map(t => (
                  <TableRow key={t.id} data-testid={`tier-row-${t.id}`}>
                    <TableCell className="text-sm font-semibold">{t.currency}</TableCell>
                    <TableCell className="text-sm mono-nums">{Number(t.min_amount).toLocaleString()}</TableCell>
                    <TableCell className="text-sm mono-nums">{t.max_amount == null ? '∞' : Number(t.max_amount).toLocaleString()}</TableCell>
                    <TableCell className="text-sm">{t.percentage_fee}%</TableCell>
                    <TableCell className="text-sm mono-nums">{t.fixed_fee || 0}</TableCell>
                    <TableCell className="text-sm capitalize">{t.provider}</TableCell>
                    <TableCell className="text-sm capitalize">{(t.applies_to || '').replace(/_/g, ' ')}</TableCell>
                    <TableCell><span className={`text-xs px-2 py-0.5 rounded-full ${t.active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>{t.active ? 'active' : 'inactive'}</span></TableCell>
                    <TableCell className="min-w-[170px]">
                      <div className="flex flex-col sm:flex-row gap-1">
                        <Button size="sm" variant="outline" className="w-full sm:w-auto" onClick={() => toggleActive(t)} data-testid={`tier-toggle-${t.id}`}>{t.active ? 'Disable' : 'Enable'}</Button>
                        <Button size="sm" variant="outline" className="w-full sm:w-auto text-red-600" onClick={() => deleteTier(t.id)} data-testid={`tier-delete-${t.id}`}>Delete</Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <div className="pt-3 border-t border-slate-100">
          <p className="text-sm font-medium text-slate-700 mb-2">Add new tier</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">Currency</Label>
              <Select value={form.currency} onValueChange={v => u('currency', v)}>
                <SelectTrigger className="mt-1" data-testid="tier-currency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['UGX','USD','KES','TZS','RWF','GBP','EUR','NGN'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Min amount</Label>
              <Input type="number" value={form.min_amount} onChange={e => u('min_amount', e.target.value)} placeholder="0" className="mt-1" data-testid="tier-min" />
            </div>
            <div>
              <Label className="text-xs">Max amount (blank = ∞)</Label>
              <Input type="number" value={form.max_amount} onChange={e => u('max_amount', e.target.value)} placeholder="20000" className="mt-1" data-testid="tier-max" />
            </div>
            <div>
              <Label className="text-xs">Percentage %</Label>
              <Input type="number" step="0.1" value={form.percentage_fee} onChange={e => u('percentage_fee', e.target.value)} placeholder="5" className="mt-1" data-testid="tier-pct" />
            </div>
            <div>
              <Label className="text-xs">Fixed fee</Label>
              <Input type="number" step="0.01" value={form.fixed_fee} onChange={e => u('fixed_fee', e.target.value)} placeholder="0" className="mt-1" data-testid="tier-fixed" />
            </div>
            <div>
              <Label className="text-xs">Gateway</Label>
              <Select value={form.provider} onValueChange={v => u('provider', v)}>
                <SelectTrigger className="mt-1" data-testid="tier-provider"><SelectValue /></SelectTrigger>
                  <SelectContent>
                  <SelectItem value="all">All gateways</SelectItem>
                  <SelectItem value="flutterwave">Flutterwave</SelectItem>
                  <SelectItem value="pesapal">Pesapal</SelectItem>
                  <SelectItem value="yopayments">Yo! Payments</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Applies to</Label>
              <Select value={form.applies_to} onValueChange={v => u('applies_to', v)}>
                <SelectTrigger className="mt-1" data-testid="tier-applies"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="payment_received">Payment received</SelectItem>
                  <SelectItem value="withdrawal">Withdrawal</SelectItem>
                  <SelectItem value="transfer_out">Transfer out</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button onClick={addTier} disabled={adding} className="bg-emerald-700 hover:bg-emerald-800 text-white w-full" data-testid="tier-add-btn">{adding ? 'Adding…' : 'Add tier'}</Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function CommissionsTab() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [enabledCurrencies, setEnabledCurrencies] = useState([]);

  useEffect(() => {
    api.get('/admin/settings').then(r => {
      setSettings(r.data);
      setEnabledCurrencies(r.data?.enabled_currencies || ['UGX', 'USD', 'KES', 'TZS', 'RWF']);
    }).catch(() => {});
  }, []);

  const u = (k, v) => setSettings(p => ({ ...p, [k]: v }));
  const updateCurrencyComm = (curr, field, value) => {
    setSettings(p => {
      const cc = { ...(p.currency_commissions || {}) };
      cc[curr] = { ...(cc[curr] || {}), [field]: value };
      return { ...p, currency_commissions: cc };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/admin/settings', settings);
      toast.success('Commission settings saved!');
    } catch (err) {
      logAdminSaveFailure('Commission settings save', err);
      toast.error(formatAdminApiError(err, 'Failed to save commission settings'));
    }
    finally { setSaving(false); }
  };

  if (!settings) return <div className="flex items-center justify-center h-40"><div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" /></div>;

  const cc = settings.currency_commissions || {};

  return (
    <div className="space-y-6 max-w-4xl">
      <CommissionTiersSection />

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl" data-testid="fee-payer-feature-card">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Outfit' }}>"Who pays the fee" feature</CardTitle>
          <p className="text-xs text-slate-500 mt-1">Allow users to choose whether fees are deducted from their received amount or added on top of the customer's payment.</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={!!settings.fee_payer_feature_enabled} onChange={e => u('fee_payer_feature_enabled', e.target.checked)} data-testid="fee-payer-feature-toggle" className="w-4 h-4" />
            <span className="text-sm text-slate-700">Allow users to choose who pays the transaction fee</span>
          </label>
          <div>
            <Label className="text-xs">Default fee payer when user has not chosen</Label>
            <Select value={settings.default_fee_payer || 'merchant'} onValueChange={v => u('default_fee_payer', v)}>
              <SelectTrigger className="mt-1 w-60" data-testid="default-fee-payer-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="merchant">Merchant pays (deducted)</SelectItem>
                <SelectItem value="customer">Customer pays (added on top)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl" data-testid="per-currency-commission-card">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Outfit' }}>Per-Currency Commission Rates</CardTitle>
          <p className="text-xs text-slate-500 mt-1">Used when no commission tier matches. Override default fees for each currency.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="overflow-x-auto">
            <Table className="min-w-[820px]">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Currency</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Payment %</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Payment Fixed</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Withdrawal %</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Withdrawal Fixed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {enabledCurrencies.map((curr) => {
                  const row = cc[curr] || {};
                  return (
                    <TableRow key={curr} data-testid={`comm-row-${curr}`}>
                      <TableCell className="font-semibold text-slate-900">{curr}</TableCell>
                      <TableCell><Input type="number" step="0.1" value={row.payment_commission_pct ?? ''} onChange={e => updateCurrencyComm(curr, 'payment_commission_pct', parseFloat(e.target.value) || 0)} className="w-24" data-testid={`comm-${curr}-pay-pct`} /></TableCell>
                      <TableCell><Input type="number" step="0.01" value={row.payment_fixed_fee ?? ''} onChange={e => updateCurrencyComm(curr, 'payment_fixed_fee', parseFloat(e.target.value) || 0)} className="w-24" data-testid={`comm-${curr}-pay-fixed`} /></TableCell>
                      <TableCell><Input type="number" step="0.1" value={row.withdrawal_fee_pct ?? ''} onChange={e => updateCurrencyComm(curr, 'withdrawal_fee_pct', parseFloat(e.target.value) || 0)} className="w-24" data-testid={`comm-${curr}-w-pct`} /></TableCell>
                      <TableCell><Input type="number" step="0.01" value={row.withdrawal_fixed_fee ?? ''} onChange={e => updateCurrencyComm(curr, 'withdrawal_fixed_fee', parseFloat(e.target.value) || 0)} className="w-24" data-testid={`comm-${curr}-w-fixed`} /></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader><CardTitle style={{ fontFamily: 'Outfit' }}>Gateway-Specific Fees</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Flutterwave Receive Fee (%)</Label>
              <Input data-testid="comm-fw-pct" type="number" step="0.1" value={settings.flutterwave_receive_fee_pct || 0} onChange={e => u('flutterwave_receive_fee_pct', parseFloat(e.target.value))} className="mt-1" />
            </div>
            <div>
              <Label>Flutterwave Fixed Fee</Label>
              <Input data-testid="comm-fw-fixed" type="number" value={settings.flutterwave_receive_fixed_fee || 0} onChange={e => u('flutterwave_receive_fixed_fee', parseFloat(e.target.value))} className="mt-1" />
            </div>
            <div>
              <Label>Pesapal Receive Fee (%)</Label>
              <Input data-testid="comm-pp-pct" type="number" step="0.1" value={settings.pesapal_receive_fee_pct || 0} onChange={e => u('pesapal_receive_fee_pct', parseFloat(e.target.value))} className="mt-1" />
            </div>
            <div>
              <Label>Pesapal Fixed Fee</Label>
              <Input data-testid="comm-pp-fixed" type="number" value={settings.pesapal_receive_fixed_fee || 0} onChange={e => u('pesapal_receive_fixed_fee', parseFloat(e.target.value))} className="mt-1" />
            </div>
            <div>
              <Label>Yo! Payments Receive Fee (%)</Label>
              <Input data-testid="comm-yo-pct" type="number" step="0.1" value={settings.yopayments_receive_fee_pct || 0} onChange={e => u('yopayments_receive_fee_pct', parseFloat(e.target.value))} className="mt-1" />
            </div>
            <div>
              <Label>Yo! Payments Fixed Fee</Label>
              <Input data-testid="comm-yo-fixed" type="number" value={settings.yopayments_receive_fixed_fee || 0} onChange={e => u('yopayments_receive_fixed_fee', parseFloat(e.target.value))} className="mt-1" />
            </div>
            <div>
              <Label>Yo! Payments Minimum Transaction</Label>
              <Input data-testid="comm-yo-min" type="number" value={settings.yopayments_min_transaction_amount ?? 1000} onChange={e => u('yopayments_min_transaction_amount', parseFloat(e.target.value) || 0)} className="mt-1" />
            </div>
            <div>
              <Label>Yo! Payments Maximum Transaction</Label>
              <Input data-testid="comm-yo-max" type="number" value={settings.yopayments_max_transaction_amount ?? 0} onChange={e => u('yopayments_max_transaction_amount', parseFloat(e.target.value) || 0)} className="mt-1" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Button onClick={save} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="save-commissions-btn">
        {saving ? 'Saving...' : 'Save Commission Settings'}
      </Button>
    </div>
  );
}


function SettingsTab() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [archiveMessage, setArchiveMessage] = useState('');

  useEffect(() => { api.get('/admin/settings').then(r => setSettings(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!settings || location.hash !== '#audit-retention') return;
    const timer = window.setTimeout(() => {
      document.getElementById('audit-retention-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
    return () => window.clearTimeout(timer);
  }, [settings, location.hash]);

  const update = (k, v) => setSettings(p => ({ ...p, [k]: v }));
  const updateWithdrawalFee = (field, value) => setSettings(p => ({ ...p, [field]: value }));
  const updateMinimumWithdrawal = (method, currency, value) => setSettings(p => ({
    ...p,
    min_withdrawal_by_method: {
      ...(p.min_withdrawal_by_method || {}),
      [method]: {
        ...((p.min_withdrawal_by_method || {})[method] || {}),
        [currency]: value,
      },
    },
  }));
  const canManagePayoutSettings = user?.is_admin === true
    || user?.admin_role === 'super_admin'
    || (user?.admin_permissions || []).includes('can_manage_payout_settings');
  const payoutSettingsFields = [
    'enable_api_payouts',
    'payout_provider',
    'payout_otp_recipient_email',
    'require_super_admin_approval_for_payouts_above',
    'daily_payout_limit',
    'payout_otp_expiry_minutes',
    'payout_otp_resend_limit',
    'allowed_payout_methods',
    'require_payout_otp_every_time',
    'payout_status_checks_enabled',
    'flutterwave_split_enabled',
    'require_kyc_before_subaccount_creation',
    'auto_create_subaccount_after_kyc',
    'allow_manual_subaccount_id',
    'split_subaccount_fallback_mode',
    'default_platform_commission_percentage',
    'default_platform_commission_flat',
    'default_split_type',
    'default_split_value',
    'enable_manual_withdrawals',
    'enable_auto_withdrawals',
    'enable_yopayments_withdrawals',
    'enable_flutterwave_withdrawals',
    'default_withdrawal_provider',
    'minimum_withdrawal_amount',
    'maximum_withdrawal_amount',
    'withdrawal_fee',
    'payout_approval_required',
    'auto_withdraw_schedule',
    'prevent_split_settled_withdrawals',
    'require_kyc_before_withdrawal',
    'require_email_otp_before_withdrawal',
    'require_admin_otp_before_payout',
    'allow_only_super_admin_to_approve_payouts',
  ];
  const togglePayoutMethod = (method) => setSettings(p => {
    const current = Array.isArray(p.allowed_payout_methods) && p.allowed_payout_methods.length ? p.allowed_payout_methods : ['mobile_money', 'bank_transfer'];
    return {
      ...p,
      allowed_payout_methods: current.includes(method) ? current.filter(x => x !== method) : [...current, method],
    };
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = canManagePayoutSettings
        ? settings
        : Object.fromEntries(Object.entries(settings || {}).filter(([key]) => !payoutSettingsFields.includes(key)));
      await api.put('/admin/settings', payload);
      toast.success('Settings saved!');
    } catch (err) {
      logAdminSaveFailure('Admin settings save', err);
      toast.error(formatAdminApiError(err, 'Failed to save settings'));
    } finally {
      setSaving(false);
    }
  };

  const archiveAuditLogs = async () => {
    setArchiving(true);
    setArchiveMessage('');
    try {
      const { data } = await api.post('/admin/audit-logs/archive', {
        retention_days: settings.audit_log_retention_days ?? 180,
      });
      const archived = data?.archived ?? 0;
      const deleted = data?.deleted ?? 0;
      const collection = data?.collection || 'audit_logs_archive';
      const cutoff = data?.cutoff || '';
      if (data?.last_archived_at) {
        setSettings(prev => ({
          ...prev,
          audit_log_last_archived_at: data.last_archived_at,
          audit_log_last_archived_count: data.last_archived_count ?? deleted,
        }));
      }
      setArchiveMessage(`Archived ${archived} logs into ${collection}${deleted ? ` and removed ${deleted} from the live log` : ''}${cutoff ? ` (cutoff ${cutoff})` : ''}.`);
      toast.success('Audit logs archived');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to archive audit logs');
    } finally {
      setArchiving(false);
    }
  };

  if (!settings) return <div className="flex items-center justify-center h-40"><div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" /></div>;
  const lastArchivedLabel = settings.audit_log_last_archived_at
    ? `${new Date(settings.audit_log_last_archived_at).toLocaleString()}${settings.audit_log_last_archived_count ? ` • ${settings.audit_log_last_archived_count} logs archived` : ''}`
    : 'Not archived yet';

  return (
    <div className="space-y-6 max-w-2xl">
      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Outfit' }}>Payment Charges</CardTitle>
          <p className="text-xs text-slate-500 mt-1">These settings apply to standard payment processing fees.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Payment Commission (%)</Label>
              <Input data-testid="setting-payment-pct" type="number" step="0.1" value={settings.payment_commission_pct} onChange={e => update('payment_commission_pct', parseFloat(e.target.value))} className="mt-1" />
            </div>
            <div>
              <Label>Payment Fixed Fee</Label>
              <Input data-testid="setting-payment-fee" type="number" value={settings.payment_fixed_fee} onChange={e => update('payment_fixed_fee', parseFloat(e.target.value))} className="mt-1" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Outfit' }}>Withdrawal Charges</CardTitle>
          <p className="text-xs text-slate-500 mt-1">Mobile Money keeps the normal fee structure while bank withdrawals can use separate charges. Legacy withdrawal fees remain as fallback values.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
              <p className="text-sm font-semibold text-slate-900">Mobile Money</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Withdrawal Fee (%)</Label>
                  <Input data-testid="setting-withdrawal-mobile-pct" type="number" step="0.1" value={settings.withdrawal_fee_pct_mobile_money ?? settings.withdrawal_fee_pct ?? 1} onChange={e => updateWithdrawalFee('withdrawal_fee_pct_mobile_money', parseFloat(e.target.value))} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">Fixed Fee</Label>
                  <Input data-testid="setting-withdrawal-mobile-fixed" type="number" value={settings.withdrawal_fixed_fee_mobile_money ?? settings.withdrawal_fixed_fee ?? 500} onChange={e => updateWithdrawalFee('withdrawal_fixed_fee_mobile_money', parseFloat(e.target.value))} className="mt-1" />
                </div>
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
              <p className="text-sm font-semibold text-slate-900">Bank Transfer</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Withdrawal Fee (%)</Label>
                  <Input data-testid="setting-withdrawal-bank-pct" type="number" step="0.1" value={settings.withdrawal_fee_pct_bank ?? settings.withdrawal_fee_pct ?? 1} onChange={e => updateWithdrawalFee('withdrawal_fee_pct_bank', parseFloat(e.target.value))} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">Fixed Fee</Label>
                  <Input data-testid="setting-withdrawal-bank-fixed" type="number" value={settings.withdrawal_fixed_fee_bank ?? settings.withdrawal_fixed_fee ?? 500} onChange={e => updateWithdrawalFee('withdrawal_fixed_fee_bank', parseFloat(e.target.value))} className="mt-1" />
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Outfit' }}>Minimum Withdrawals</CardTitle>
          <p className="text-xs text-slate-500 mt-1">Set separate minimums for Mobile Money and Bank withdrawals. Other currencies still fall back to the legacy minimum settings.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
              <p className="text-sm font-semibold text-slate-900">Mobile Money Minimums</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">UGX Minimum Mobile Money Withdrawal</Label>
                  <Input data-testid="setting-min-mobile-ugx" type="number" value={settings.min_withdrawal_by_method?.mobile_money?.UGX ?? settings.min_withdrawal?.UGX ?? 50000} onChange={e => updateMinimumWithdrawal('mobile_money', 'UGX', parseFloat(e.target.value))} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">USD Minimum Mobile Money Withdrawal</Label>
                  <Input data-testid="setting-min-mobile-usd" type="number" value={settings.min_withdrawal_by_method?.mobile_money?.USD ?? settings.min_withdrawal?.USD ?? 20} onChange={e => updateMinimumWithdrawal('mobile_money', 'USD', parseFloat(e.target.value))} className="mt-1" />
                </div>
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
              <p className="text-sm font-semibold text-slate-900">Bank Minimums</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">UGX Minimum Bank Withdrawal</Label>
                  <Input data-testid="setting-min-bank-ugx" type="number" value={settings.min_withdrawal_by_method?.bank_transfer?.UGX ?? settings.min_withdrawal?.UGX ?? 50000} onChange={e => updateMinimumWithdrawal('bank_transfer', 'UGX', parseFloat(e.target.value))} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">USD Minimum Bank Withdrawal</Label>
                  <Input data-testid="setting-min-bank-usd" type="number" value={settings.min_withdrawal_by_method?.bank_transfer?.USD ?? settings.min_withdrawal?.USD ?? 20} onChange={e => updateMinimumWithdrawal('bank_transfer', 'USD', parseFloat(e.target.value))} className="mt-1" />
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl" data-testid="payout-security-card">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Outfit' }}>Payout Security</CardTitle>
          <p className="text-xs text-slate-500 mt-1">Control API payout approval rules and which withdrawal methods can be sent from the admin panel.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          {!canManagePayoutSettings && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              You can view payout settings, but only admins with payout settings permission can change them.
            </div>
          )}
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={!!settings.enable_api_payouts} onChange={e => update('enable_api_payouts', e.target.checked)} className="w-4 h-4" data-testid="enable-api-payouts" disabled={!canManagePayoutSettings} />
            <span className="text-sm text-slate-700">Enable API payouts</span>
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Payout provider</Label>
              <Select value={settings.payout_provider || 'auto'} onValueChange={v => update('payout_provider', v)} disabled={!canManagePayoutSettings}>
                <SelectTrigger className="mt-1" data-testid="payout-provider-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto (Default gateway)</SelectItem>
                  <SelectItem value="flutterwave">Flutterwave</SelectItem>
                  <SelectItem value="pesapal">Pesapal</SelectItem>
                  <SelectItem value="yopayments">Yo! Payments</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Payout OTP recipient email</Label>
              <Input
                value={settings.payout_otp_recipient_email || ''}
                onChange={e => update('payout_otp_recipient_email', e.target.value)}
                placeholder={user?.email || 'admin@jjuma.com'}
                className="mt-1"
                data-testid="payout-otp-recipient"
                disabled={!canManagePayoutSettings}
              />
            </div>
            <div>
              <Label className="text-xs">Require super admin approval above</Label>
              <Input type="number" min="0" value={settings.require_super_admin_approval_for_payouts_above ?? 0} onChange={e => update('require_super_admin_approval_for_payouts_above', parseFloat(e.target.value) || 0)} className="mt-1" data-testid="payout-threshold" disabled={!canManagePayoutSettings} />
            </div>
            <div>
              <Label className="text-xs">Daily payout limit</Label>
              <Input type="number" min="0" value={settings.daily_payout_limit ?? 0} onChange={e => update('daily_payout_limit', parseFloat(e.target.value) || 0)} className="mt-1" data-testid="payout-daily-limit" disabled={!canManagePayoutSettings} />
            </div>
            <div>
              <Label className="text-xs">Payout OTP expiry (minutes)</Label>
              <Input type="number" min="5" max="120" value={settings.payout_otp_expiry_minutes ?? 10} onChange={e => update('payout_otp_expiry_minutes', parseInt(e.target.value) || 10)} className="mt-1" data-testid="payout-otp-expiry" disabled={!canManagePayoutSettings} />
            </div>
            <div>
              <Label className="text-xs">Payout OTP resend limit</Label>
              <Input type="number" min="1" max="20" value={settings.payout_otp_resend_limit ?? 5} onChange={e => update('payout_otp_resend_limit', parseInt(e.target.value) || 5)} className="mt-1" data-testid="payout-otp-resend-limit" disabled={!canManagePayoutSettings} />
            </div>
          </div>
          <div>
            <Label className="text-xs">Allowed payout methods</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
              {[
                ['mobile_money', 'Mobile Money'],
                ['bank_transfer', 'Bank Transfer'],
              ].map(([value, label]) => (
                <label key={value} className="flex items-center gap-3 cursor-pointer rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <input type="checkbox" checked={(settings.allowed_payout_methods || ['mobile_money', 'bank_transfer']).includes(value)} onChange={() => togglePayoutMethod(value)} className="w-4 h-4" disabled={!canManagePayoutSettings} data-testid={`allowed-payout-${value}`} />
                  <span className="text-sm text-slate-700">{label}</span>
                </label>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={!!settings.require_payout_otp_every_time} onChange={e => update('require_payout_otp_every_time', e.target.checked)} className="w-4 h-4" data-testid="require-payout-otp" disabled={!canManagePayoutSettings} />
            <span className="text-sm text-slate-700">Require OTP for every payout</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={!!settings.payout_status_checks_enabled} onChange={e => update('payout_status_checks_enabled', e.target.checked)} className="w-4 h-4" data-testid="payout-status-checks" disabled={!canManagePayoutSettings} />
            <span className="text-sm text-slate-700">Enable payout status verification</span>
          </label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-[11px] text-slate-500">
            <p>OTP every payout adds a security step before real money moves.</p>
            <p>Auto uses the default gateway when a payout provider is not explicitly selected.</p>
            <p>Disabled payout methods will not appear in the admin payout flow.</p>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Outfit' }}>Settlement Controls</CardTitle>
          <p className="text-xs text-slate-500 mt-1">Manage Flutterwave split settlement rules and default merchant withdrawal behavior.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={!!settings.flutterwave_split_enabled} onChange={e => update('flutterwave_split_enabled', e.target.checked)} className="w-4 h-4" disabled={!canManagePayoutSettings} data-testid="setting-flw-split-enabled" />
            <span className="text-sm text-slate-700">Enable Flutterwave split payments globally</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={!!settings.require_kyc_before_subaccount_creation} onChange={e => update('require_kyc_before_subaccount_creation', e.target.checked)} className="w-4 h-4" disabled={!canManagePayoutSettings} data-testid="setting-require-kyc-subaccount" />
            <span className="text-sm text-slate-700">Require KYC approval before subaccount creation</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={!!settings.auto_create_subaccount_after_kyc} onChange={e => update('auto_create_subaccount_after_kyc', e.target.checked)} className="w-4 h-4" disabled={!canManagePayoutSettings} data-testid="setting-auto-create-subaccount" />
            <span className="text-sm text-slate-700">Auto create subaccount after KYC approval</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={!!settings.allow_manual_subaccount_id} onChange={e => update('allow_manual_subaccount_id', e.target.checked)} className="w-4 h-4" disabled={!canManagePayoutSettings} data-testid="setting-allow-manual-subaccount" />
            <span className="text-sm text-slate-700">Allow manual Flutterwave subaccount ID entry</span>
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Split fallback behavior</Label>
              <Select value={settings.split_subaccount_fallback_mode || 'wallet'} onValueChange={v => update('split_subaccount_fallback_mode', v)} disabled={!canManagePayoutSettings}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="block">Block checkout</SelectItem>
                  <SelectItem value="wallet">Use JJuma wallet settlement</SelectItem>
                  <SelectItem value="manual">Manual review</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Default split type</Label>
              <Select value={settings.default_split_type || 'percentage'} onValueChange={v => update('default_split_type', v)} disabled={!canManagePayoutSettings}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">Percentage</SelectItem>
                  <SelectItem value="flat">Flat</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Default split value</Label>
              <Input type="number" min="0" value={settings.default_split_value ?? 0} onChange={e => update('default_split_value', parseFloat(e.target.value) || 0)} className="mt-1" disabled={!canManagePayoutSettings} />
            </div>
            <div>
              <Label className="text-xs">Default platform commission %</Label>
              <Input type="number" min="0" value={settings.default_platform_commission_percentage ?? 0} onChange={e => update('default_platform_commission_percentage', parseFloat(e.target.value) || 0)} className="mt-1" disabled={!canManagePayoutSettings} />
            </div>
            <div>
              <Label className="text-xs">Default platform commission flat</Label>
              <Input type="number" min="0" value={settings.default_platform_commission_flat ?? 0} onChange={e => update('default_platform_commission_flat', parseFloat(e.target.value) || 0)} className="mt-1" disabled={!canManagePayoutSettings} />
            </div>
            <div>
              <Label className="text-xs">Default payout provider</Label>
              <Select value={settings.default_withdrawal_provider || 'flutterwave'} onValueChange={v => update('default_withdrawal_provider', v)} disabled={!canManagePayoutSettings}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="flutterwave">Flutterwave</SelectItem>
                  <SelectItem value="yopayments">Yo! Payments</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Auto withdraw schedule</Label>
              <Select value={settings.auto_withdraw_schedule || 'manual'} onValueChange={v => update('auto_withdraw_schedule', v)} disabled={!canManagePayoutSettings}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual only</SelectItem>
                  <SelectItem value="instant">Instant</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Minimum withdrawal amount</Label>
              <Input type="number" min="0" value={settings.minimum_withdrawal_amount ?? 0} onChange={e => update('minimum_withdrawal_amount', parseFloat(e.target.value) || 0)} className="mt-1" disabled={!canManagePayoutSettings} />
            </div>
            <div>
              <Label className="text-xs">Maximum withdrawal amount</Label>
              <Input type="number" min="0" value={settings.maximum_withdrawal_amount ?? 0} onChange={e => update('maximum_withdrawal_amount', parseFloat(e.target.value) || 0)} className="mt-1" disabled={!canManagePayoutSettings} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="flex items-center gap-3 cursor-pointer rounded-xl border border-slate-200 bg-slate-50 p-3">
              <input type="checkbox" checked={!!settings.enable_manual_withdrawals} onChange={e => update('enable_manual_withdrawals', e.target.checked)} className="w-4 h-4" disabled={!canManagePayoutSettings} />
              <span className="text-sm text-slate-700">Enable manual withdrawals</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer rounded-xl border border-slate-200 bg-slate-50 p-3">
              <input type="checkbox" checked={!!settings.enable_auto_withdrawals} onChange={e => update('enable_auto_withdrawals', e.target.checked)} className="w-4 h-4" disabled={!canManagePayoutSettings} />
              <span className="text-sm text-slate-700">Enable automatic withdrawals</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer rounded-xl border border-slate-200 bg-slate-50 p-3">
              <input type="checkbox" checked={!!settings.enable_flutterwave_withdrawals} onChange={e => update('enable_flutterwave_withdrawals', e.target.checked)} className="w-4 h-4" disabled={!canManagePayoutSettings} />
              <span className="text-sm text-slate-700">Enable Flutterwave withdrawals</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer rounded-xl border border-slate-200 bg-slate-50 p-3">
              <input type="checkbox" checked={!!settings.enable_yopayments_withdrawals} onChange={e => update('enable_yopayments_withdrawals', e.target.checked)} className="w-4 h-4" disabled={!canManagePayoutSettings} />
              <span className="text-sm text-slate-700">Enable Yo! Payments withdrawals</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer rounded-xl border border-slate-200 bg-slate-50 p-3">
              <input type="checkbox" checked={!!settings.prevent_split_settled_withdrawals} onChange={e => update('prevent_split_settled_withdrawals', e.target.checked)} className="w-4 h-4" disabled={!canManagePayoutSettings} />
              <span className="text-sm text-slate-700">Prevent withdrawal for split-settled transactions</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer rounded-xl border border-slate-200 bg-slate-50 p-3">
              <input type="checkbox" checked={!!settings.require_kyc_before_withdrawal} onChange={e => update('require_kyc_before_withdrawal', e.target.checked)} className="w-4 h-4" disabled={!canManagePayoutSettings} />
              <span className="text-sm text-slate-700">Require KYC before withdrawal</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer rounded-xl border border-slate-200 bg-slate-50 p-3">
              <input type="checkbox" checked={!!settings.require_email_otp_before_withdrawal} onChange={e => update('require_email_otp_before_withdrawal', e.target.checked)} className="w-4 h-4" disabled={!canManagePayoutSettings} />
              <span className="text-sm text-slate-700">Require email/OTP confirmation before withdrawal</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer rounded-xl border border-slate-200 bg-slate-50 p-3">
              <input type="checkbox" checked={!!settings.require_admin_otp_before_payout} onChange={e => update('require_admin_otp_before_payout', e.target.checked)} className="w-4 h-4" disabled={!canManagePayoutSettings} />
              <span className="text-sm text-slate-700">Require admin OTP before approving payout</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer rounded-xl border border-slate-200 bg-slate-50 p-3">
              <input type="checkbox" checked={!!settings.allow_only_super_admin_to_approve_payouts} onChange={e => update('allow_only_super_admin_to_approve_payouts', e.target.checked)} className="w-4 h-4" disabled={!canManagePayoutSettings} />
              <span className="text-sm text-slate-700">Allow only super admin to approve payouts</span>
            </label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[11px] text-slate-500">
            <p>Split-settled payments remain paid to the merchant subaccount and never enter the JJuma withdrawable wallet.</p>
            <p>Wallet fallback keeps payments available for merchant withdrawal when a subaccount is unavailable.</p>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl" id="audit-retention-card" data-testid="audit-retention-card">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Outfit' }}>Audit Log Retention</CardTitle>
          <p className="text-xs text-slate-500 mt-1">Keep recent logs in the live audit view and archive older entries automatically.</p>
          <button
            type="button"
            onClick={() => navigate('/admin/audit-logs')}
            className="mt-2 inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-[11px] font-medium text-slate-600 transition hover:bg-slate-200 hover:text-slate-900"
            data-testid="audit-last-archived-chip"
          >
            Last archived: <span className="ml-1 font-semibold text-slate-800">{lastArchivedLabel}</span>
          </button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Retention window (days)</Label>
              <Input
                type="number"
                min="1"
                max="3650"
                value={settings.audit_log_retention_days ?? 180}
                onChange={e => update('audit_log_retention_days', Math.max(1, parseInt(e.target.value) || 180))}
                className="mt-1"
                data-testid="audit-retention-days"
              />
            </div>
            <div>
              <Label>Archive on startup</Label>
              <div className="mt-2 flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                <input
                  type="checkbox"
                  checked={!!settings.audit_log_archive_on_startup}
                  onChange={e => update('audit_log_archive_on_startup', e.target.checked)}
                  className="w-4 h-4"
                  data-testid="audit-retention-startup"
                />
                <span className="text-sm text-slate-700">Automatically archive old audit logs when the API starts</span>
              </div>
            </div>
          </div>
          <p className="text-xs text-slate-500">Older logs move into the archive collection so the live audit view stays fast and focused.</p>
          {archiveMessage && (
            <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
              {archiveMessage}
            </div>
          )}
          <Button
            type="button"
            variant="outline"
            onClick={archiveAuditLogs}
            disabled={archiving}
            className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
            data-testid="archive-audit-logs-btn"
          >
            {archiving ? 'Archiving...' : 'Archive old audit logs now'}
          </Button>
        </CardContent>
      </Card>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl" data-testid="email-otp-settings-card">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Outfit' }}>Email Verification</CardTitle>
          <p className="text-xs text-slate-500 mt-1">Control when users must verify their email via OTP.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-xs">Verification document notification email(s)</Label>
            <Input
              value={settings.verification_notification_emails || ''}
              onChange={e => update('verification_notification_emails', e.target.value)}
              placeholder="hi@jjuma.com, compliance@jjuma.com"
              className="mt-1"
              data-testid="verification-notification-emails"
            />
            <p className="text-[11px] text-slate-500 mt-1">Comma-separated emails receive KYC, ID, passport, selfie, and business verification notifications.</p>
          </div>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={!!settings.email_verification_enabled} onChange={e => update('email_verification_enabled', e.target.checked)} className="w-4 h-4" data-testid="email-verif-enabled" />
            <span className="text-sm text-slate-700">Enable email verification</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={!!settings.require_verification_before_login} onChange={e => update('require_verification_before_login', e.target.checked)} className="w-4 h-4" data-testid="email-verif-before-login" disabled={!settings.email_verification_enabled} />
            <span className="text-sm text-slate-700">Require verification on first login (block dashboard until verified)</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={!!settings.require_verification_before_receive} onChange={e => update('require_verification_before_receive', e.target.checked)} className="w-4 h-4" data-testid="email-verif-before-receive" disabled={!settings.email_verification_enabled} />
            <span className="text-sm text-slate-700">Require verification before receiving payments</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={!!settings.require_verification_before_withdrawal} onChange={e => update('require_verification_before_withdrawal', e.target.checked)} className="w-4 h-4" data-testid="email-verif-before-withdrawal" disabled={!settings.email_verification_enabled} />
            <span className="text-sm text-slate-700">Require verification before withdrawal</span>
          </label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-1">
            <label className="flex items-center gap-3 cursor-pointer rounded-xl border border-slate-200 bg-slate-50 p-3">
              <input type="checkbox" checked={!!settings.otp_every_login_enabled} onChange={e => update('otp_every_login_enabled', e.target.checked)} className="w-4 h-4" data-testid="otp-every-login-enabled" />
              <span className="text-sm text-slate-700">OTP every login</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer rounded-xl border border-slate-200 bg-slate-50 p-3">
              <input type="checkbox" checked={!!settings.otp_new_ip_enabled} onChange={e => update('otp_new_ip_enabled', e.target.checked)} className="w-4 h-4" data-testid="otp-new-ip-enabled" />
              <span className="text-sm text-slate-700">OTP for new IP</span>
            </label>
            <div>
              <Label className="text-xs">Trusted IP duration (hours)</Label>
              <Input
                type="number"
                min="1"
                value={settings.trusted_ip_duration_hours ?? 24}
                onChange={e => update('trusted_ip_duration_hours', parseInt(e.target.value) || 24)}
                className="mt-1"
                data-testid="trusted-ip-duration"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[11px] text-slate-500">
            <p>OTP every login increases security but may add friction.</p>
            <p>New IP OTP asks users to verify when logging in from a new network.</p>
            <p>Trusted IP duration controls how long the verified IP is remembered.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
            <div>
              <Label className="text-xs">Verification method</Label>
              <Select value={settings.email_verification_method || 'otp'} onValueChange={v => update('email_verification_method', v)}>
                <SelectTrigger className="mt-1" data-testid="email-verif-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="otp">OTP code (6 digits)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">OTP expiry (minutes)</Label>
              <Input type="number" min="5" max="120" value={settings.email_otp_expiry_minutes ?? 30} onChange={e => update('email_otp_expiry_minutes', parseInt(e.target.value) || 30)} className="mt-1" data-testid="email-verif-expiry" />
            </div>
            <div>
              <Label className="text-xs">Resend limit (per 10 min)</Label>
              <Input type="number" min="1" max="20" value={settings.email_otp_resend_limit ?? 5} onChange={e => update('email_otp_resend_limit', parseInt(e.target.value) || 5)} className="mt-1" data-testid="email-verif-resend-limit" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Button onClick={handleSave} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="save-admin-settings-btn">
        {saving ? 'Saving...' : 'Save Settings'}
      </Button>
    </div>
  );
}

export default function AdminPanel({ defaultTab }) {
  return (
    <div className="space-y-6 pt-1 md:pt-0">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Admin Panel</h1>
        <p className="text-slate-500 text-sm mt-0.5">Manage users, transactions, and platform settings</p>
      </div>

      <Tabs defaultValue={defaultTab || "overview"}>
        <TabsList className="mb-4 gap-1 overflow-x-auto flex-nowrap justify-start" data-testid="admin-tabs">
          <TabsTrigger className="shrink-0" value="overview" data-testid="admin-tab-overview">Overview</TabsTrigger>
          <TabsTrigger className="shrink-0" value="users" data-testid="admin-tab-users">Users</TabsTrigger>
          <TabsTrigger className="shrink-0" value="transactions" data-testid="admin-tab-transactions">Transactions</TabsTrigger>
          <TabsTrigger className="shrink-0" value="withdrawals" data-testid="admin-tab-withdrawals">Withdrawals</TabsTrigger>
          <TabsTrigger className="shrink-0" value="verifications" data-testid="admin-tab-verifications">Verifications</TabsTrigger>
          <TabsTrigger className="shrink-0" value="commissions" data-testid="admin-tab-commissions">Commissions</TabsTrigger>
          <TabsTrigger className="shrink-0" value="settings" data-testid="admin-tab-settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="overview"><OverviewTab /></TabsContent>
        <TabsContent value="users"><UsersTab /></TabsContent>
        <TabsContent value="transactions"><AdminTransactionsTab /></TabsContent>
        <TabsContent value="withdrawals"><WithdrawalsTab /></TabsContent>
        <TabsContent value="verifications"><VerificationsTab /></TabsContent>
        <TabsContent value="commissions"><CommissionsTab /></TabsContent>
        <TabsContent value="settings"><SettingsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
